class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.onFormInput = this.onFormInput.bind(this);
    this.onFormChange = this.onFormChange.bind(this);

    const form = this.querySelector('form');
    if (form) {
      form.addEventListener('input', this.onFormInput);
      form.addEventListener('change', this.onFormChange);
      form.addEventListener('submit', this.onSubmitHandler.bind(this));
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state && typeof event.state.searchParams === 'string'
        ? event.state.searchParams
        : window.location.search.slice(1);

      if (searchParams === FacetFiltersForm.searchParamsPrev) {
        FacetFiltersForm.syncUiToSearchParams(searchParams);
        return;
      }

      // Client-managed tag/specification pages are cached after their first render.
      // Browser back/forward between pagination states should restore that finished
      // page immediately instead of replacing it with Shopify's unfiltered section
      // and running the collection-index fallback again.
      if (
        typeof window.cosmoshopRestoreCachedClientFilteredPage === 'function'
        && window.cosmoshopRestoreCachedClientFilteredPage(searchParams)
      ) {
        FacetFiltersForm.searchParamsPrev = FacetFiltersForm.normaliseSearchParams(searchParams);
        FacetFiltersForm.setLoading(false);
        FacetFiltersForm.toggleActiveFacets(false);
        FacetFiltersForm.syncUiToSearchParams(searchParams);
        return;
      }

      FacetFiltersForm.renderPage(searchParams, null, false);
    };

    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove, .active-facets__button, .active-facets__button-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
      element.toggleAttribute('aria-disabled', disable);
    });
  }

  static normaliseSearchParams(searchParams) {
    const source = searchParams instanceof URLSearchParams
      ? new URLSearchParams(searchParams.toString())
      : new URLSearchParams(String(searchParams || '').replace(/^\?/, ''));

    source.delete('sections');
    source.delete('section_id');

    for (const key of Array.from(source.keys())) {
      const values = source.getAll(key);
      source.delete(key);
      const seen = new Set();

      values.forEach((value) => {
        const cleaned = String(value == null ? '' : value).trim();
        if (cleaned === '' && key !== 'options[prefix]') return;
        const signature = `${key}\u0000${cleaned}`;
        if (seen.has(signature)) return;
        seen.add(signature);
        source.append(key, cleaned);
      });
    }

    return source.toString();
  }

  static currentForm() {
    return document.getElementById('FacetFiltersFormMobile')
      || document.querySelector('facet-filters-form form');
  }

  static searchParamsFromForm(form) {
    if (!form) return FacetFiltersForm.normaliseSearchParams(window.location.search);
    return FacetFiltersForm.cleanSearchParams(new FormData(form));
  }

  static scheduleFormRender(form, event, delay = 350, searchParamsOverride = null) {
    if (!form) return;

    // A newer filter interaction makes any in-flight response obsolete immediately.
    // Abort it now instead of letting stale results render while the 350ms debounce runs.
    if (FacetFiltersForm.renderAbortController) {
      FacetFiltersForm.renderAbortController.abort();
      FacetFiltersForm.renderAbortController = null;
      FacetFiltersForm.lastRequestedSearchParams = null;
      FacetFiltersForm.renderRequestId += 1;
      FacetFiltersForm.setLoading(false);
    }

    FacetFiltersForm.toggleActiveFacets(true);
    FacetFiltersForm.pendingForm = form;
    FacetFiltersForm.pendingEvent = event || null;
    if (searchParamsOverride !== null && searchParamsOverride !== undefined) {
      FacetFiltersForm.pendingSearchParams = FacetFiltersForm.normaliseSearchParams(searchParamsOverride);
    }
    window.clearTimeout(FacetFiltersForm.pendingSubmitTimer);
    FacetFiltersForm.pendingSubmitTimer = window.setTimeout(() => {
      FacetFiltersForm.flushScheduledFormRender();
    }, delay);
  }

  static flushScheduledFormRender(form = null, event = null) {
    const targetForm = form || FacetFiltersForm.pendingForm || FacetFiltersForm.currentForm();
    const targetEvent = event || FacetFiltersForm.pendingEvent || null;
    const pendingSearchParams = FacetFiltersForm.pendingSearchParams;

    window.clearTimeout(FacetFiltersForm.pendingSubmitTimer);
    FacetFiltersForm.pendingSubmitTimer = null;
    FacetFiltersForm.pendingForm = null;
    FacetFiltersForm.pendingEvent = null;
    FacetFiltersForm.pendingSearchParams = null;

    if (!targetForm) return false;

    // Checkbox/radio/select changes are accumulated from the committed URL plus the
    // exact controls the customer changed. This avoids stale controls from a previous
    // zero-result AJAX render being serialised back into the next request.
    const searchParams = pendingSearchParams !== null && pendingSearchParams !== undefined
      ? FacetFiltersForm.normaliseSearchParams(pendingSearchParams)
      : FacetFiltersForm.searchParamsFromForm(targetForm);

    // The drawer auto-applies checkbox and sort changes. Clicking Apply after that
    // state is already reflected in the URL should only close the drawer; it must not
    // repeat the same Section Rendering request and client-side fallback cycle. Ignore
    // the current page number when comparing because a genuine filter change still
    // intentionally resets pagination to page 1.
    const currentParams = new URLSearchParams(FacetFiltersForm.normaliseSearchParams(window.location.search));
    currentParams.delete('page');
    const currentComparableSearchParams = FacetFiltersForm.normaliseSearchParams(currentParams);

    if (searchParams === currentComparableSearchParams) {
      FacetFiltersForm.syncUiToSearchParams(searchParams);
      FacetFiltersForm.toggleActiveFacets(false);
      return false;
    }

    FacetFiltersForm.renderPage(searchParams, targetEvent);
    return true;
  }

  static setLoading(isLoading) {
    const collection = document.querySelector('#ProductGridContainer .collection');
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');

    if (collection) collection.classList.toggle('loading', isLoading);
    if (countContainer) countContainer.classList.toggle('loading', isLoading);
    if (countContainerDesktop) countContainerDesktop.classList.toggle('loading', isLoading);
  }

  static syncUiToSearchParams(searchParams) {
    if (typeof window.cosmoshopSyncFilterControlsFromSearchParams === 'function') {
      window.cosmoshopSyncFilterControlsFromSearchParams(searchParams);
    }
    if (typeof window.cosmoshopRenderActiveFilterChips === 'function') {
      window.cosmoshopRenderActiveFilterChips();
    }
  }

  static renderPage(searchParams, event = null, updateURLHash = true, options = {}) {
    const normalisedSearchParams = FacetFiltersForm.normaliseSearchParams(searchParams);
    const paginationOnly = Boolean(options && options.paginationOnly);

    // Applying the same state twice (for example, the automatic refresh followed by
    // the visible Apply button) must not create a second request or race the first one.
    if (
      normalisedSearchParams === FacetFiltersForm.lastRequestedSearchParams
      && FacetFiltersForm.renderAbortController
    ) {
      FacetFiltersForm.syncUiToSearchParams(normalisedSearchParams);
      return;
    }

    FacetFiltersForm.searchParamsPrev = normalisedSearchParams;
    FacetFiltersForm.lastRequestedSearchParams = normalisedSearchParams;

    if (FacetFiltersForm.renderAbortController) {
      FacetFiltersForm.renderAbortController.abort();
    }

    const requestController = typeof AbortController !== 'undefined' ? new AbortController() : null;
    FacetFiltersForm.renderAbortController = requestController;
    const requestId = ++FacetFiltersForm.renderRequestId;

    if (updateURLHash) FacetFiltersForm.updateURLHash(normalisedSearchParams);

    // The URL is the single source of truth. Reflect it immediately so chips and
    // checkboxes never display an older state while the product section is loading.
    FacetFiltersForm.syncUiToSearchParams(normalisedSearchParams);
    FacetFiltersForm.toggleActiveFacets(true);
    FacetFiltersForm.setLoading(true);

    // Native Search & Discovery filters are resolved by Shopify's Section Rendering
    // endpoint. Never download and scan the collection's product JSON to evaluate
    // filter.p.m.* metafields, tags, vendor, category, gender or age in the browser.
    // The legacy client grid is retained ONLY for the optional non-Shopify custom
    // "Largest savings" sort, which Shopify cannot sort on the server.
    const useClientGrid = Boolean(
      typeof window.cosmoshopShouldUseClientGridForSearch === 'function'
      && window.cosmoshopShouldUseClientGridForSearch(normalisedSearchParams)
    );

    if (useClientGrid && typeof window.cosmoshopRenderClientFilteredGridNow === 'function') {
      window.cosmoshopRenderClientFilteredGridNow({
        useCache: true,
        suppressNotice: paginationOnly,
        prefetch: true,
      });
    }

    // The custom Largest savings sort maintains its own pagination cache.
    // All standard filter results and pagination come directly from Shopify.
    if (paginationOnly && useClientGrid) {
      FacetFiltersForm.renderAbortController = null;
      FacetFiltersForm.lastRequestedSearchParams = null;
      return;
    }

    const sections = FacetFiltersForm.getSections();
    if (!sections.length) {
      FacetFiltersForm.setLoading(false);
      FacetFiltersForm.toggleActiveFacets(false);
      FacetFiltersForm.renderAbortController = null;
      FacetFiltersForm.lastRequestedSearchParams = null;
      return;
    }

    Promise.all(sections.map((section) => {
      const url = FacetFiltersForm.buildSectionRequestUrl(section.section, normalisedSearchParams);
      return FacetFiltersForm.fetchSection(url, requestController?.signal)
        .then((html) => ({ html, section }));
    }))
      .then((responses) => {
        if (requestId !== FacetFiltersForm.renderRequestId) return;
        responses.forEach(({ html }) => {
          FacetFiltersForm.renderSectionResponse(html, event, requestId, normalisedSearchParams, {
            paginationOnly,
            useClientGrid,
          });
        });
      })
      .catch((error) => {
        if (error && error.name === 'AbortError') return;
        if (requestId !== FacetFiltersForm.renderRequestId) return;

        FacetFiltersForm.setLoading(false);
        FacetFiltersForm.toggleActiveFacets(false);
        FacetFiltersForm.renderAbortController = null;
        FacetFiltersForm.lastRequestedSearchParams = null;

        // A normal navigation is the correctness fallback when Section Rendering is
        // unavailable. It preserves every selected value and Shopify's native logic.
        const targetUrl = FacetFiltersForm.buildNavigationUrl(normalisedSearchParams);
        window.location.assign(targetUrl);
      });
  }

  static cachedSection(url) {
    const entry = FacetFiltersForm.sectionResponseCache.get(url);
    if (!entry) return null;
    if ((Date.now() - entry.createdAt) > FacetFiltersForm.sectionCacheTtlMs) {
      FacetFiltersForm.sectionResponseCache.delete(url);
      return null;
    }
    // Touch the entry so the Map behaves as a small LRU cache.
    FacetFiltersForm.sectionResponseCache.delete(url);
    FacetFiltersForm.sectionResponseCache.set(url, entry);
    return entry.html;
  }

  static storeSection(url, html) {
    if (!url || !html) return;
    FacetFiltersForm.sectionResponseCache.delete(url);
    FacetFiltersForm.sectionResponseCache.set(url, { html, createdAt: Date.now() });
    while (FacetFiltersForm.sectionResponseCache.size > FacetFiltersForm.sectionCacheMaxEntries) {
      const oldest = FacetFiltersForm.sectionResponseCache.keys().next().value;
      FacetFiltersForm.sectionResponseCache.delete(oldest);
    }
  }

  static fetchSection(url, signal) {
    const cached = FacetFiltersForm.cachedSection(url);
    if (cached !== null) return Promise.resolve(cached);

    return fetch(url, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal,
    }).then((response) => {
      if (!response.ok) throw new Error(`Filter request failed with status ${response.status}`);
      return response.text();
    }).then((html) => {
      FacetFiltersForm.storeSection(url, html);
      return html;
    });
  }

  static renderSectionResponse(html, event, requestId, submittedSearchParams = '', options = {}) {
    if (requestId !== FacetFiltersForm.renderRequestId) return;
    const paginationOnly = Boolean(options && options.paginationOnly);
    const useClientGrid = Boolean(options && options.useClientGrid);
    const parsedDocument = new DOMParser().parseFromString(html, 'text/html');

    // Page changes do not alter the available filter controls. Avoid tearing down and
    // rebuilding the complete drawer just because the customer moved from page N to N+1.
    if (!paginationOnly) {
      try {
        FacetFiltersForm.renderFilters(parsedDocument, event);
      } catch (error) {
        // Product results remain usable even if optional facet markup is malformed.
      }
    }

    // When custom filtering is active, the client renderer is already running in
    // parallel and owns the product result. Rendering Shopify's intermediate grid here
    // would only add DOM work and a visible flash before being replaced again.
    if (!useClientGrid) {
      try {
        FacetFiltersForm.renderProductGridContainer(parsedDocument);
      } catch (error) {
        // Keep the existing grid if Shopify returns incomplete section markup.
      }

      try {
        FacetFiltersForm.renderProductCount(parsedDocument);
      } catch (error) {
        FacetFiltersForm.setLoading(false);
      }
    }

    FacetFiltersForm.renderAbortController = null;
    FacetFiltersForm.lastRequestedSearchParams = null;

    if (!useClientGrid) {
      FacetFiltersForm.setLoading(false);
      FacetFiltersForm.toggleActiveFacets(false);
    }
    FacetFiltersForm.syncUiToSearchParams(submittedSearchParams);

    // After the drawer has been re-rendered from Shopify, sync selected values once.
    // Do not prefetch two additional, potentially large product-grid HTML pages after
    // every filter selection: those parallel requests compete with the next filter.
    if (!paginationOnly) {
      window.setTimeout(() => FacetFiltersForm.syncUiToSearchParams(submittedSearchParams), 0);
    }
  }

  static renderProductGridContainer(parsedDocument) {
    const parsedContainer = parsedDocument.getElementById('ProductGridContainer');
    const targetContainer = document.getElementById('ProductGridContainer');

    if (!parsedContainer || !targetContainer) return;

    targetContainer.innerHTML = parsedContainer.innerHTML;
    Array.from(parsedContainer.attributes).forEach((attribute) => {
      if (attribute.name.indexOf('data-') === 0) {
        targetContainer.setAttribute(attribute.name, attribute.value);
      }
    });

    if (typeof window.cosmoshopApplyProductView === 'function') {
      window.setTimeout(window.cosmoshopApplyProductView, 0);
    }
    if (typeof window.cosmoshopSortVisibleGridByLargestSavings === 'function') {
      window.setTimeout(window.cosmoshopSortVisibleGridByLargestSavings, 0);
    }
    if (typeof window.CosmoshopRefreshProductStatusBadgeCarousels === 'function') {
      window.setTimeout(() => window.CosmoshopRefreshProductStatusBadgeCarousels(targetContainer), 0);
      window.setTimeout(() => window.CosmoshopRefreshProductStatusBadgeCarousels(targetContainer), 160);
    }
  }

  static getVisibleProductItemCount(root = document) {
    const productGrid = root.querySelector('#ProductGridContainer #product-grid');
    if (!productGrid) return 0;

    return Array.from(productGrid.children).filter((child) => child.classList && child.classList.contains('grid__item')).length;
  }

  static getCurrentPageFromURL() {
    try {
      const page = Number.parseInt(new URLSearchParams(window.location.search || '').get('page') || '1', 10);
      return Number.isFinite(page) && page > 0 ? page : 1;
    } catch (error) {
      return 1;
    }
  }

  static formatShownOfTotal(total, pageSize, visibleCount, currentPage = FacetFiltersForm.getCurrentPageFromURL()) {
    const safeTotal = Number.isFinite(total) && total > 0 ? total : 0;
    const safePageSize = Number.isFinite(pageSize) && pageSize > 0 ? pageSize : visibleCount;
    const safeVisibleCount = Number.isFinite(visibleCount) && visibleCount > 0
      ? visibleCount
      : Math.min(safePageSize || 0, safeTotal);
    const safeCurrentPage = Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1;

    if (!safeTotal) return '0 of 0';

    let shown = ((safeCurrentPage - 1) * (safePageSize || safeVisibleCount)) + safeVisibleCount;
    if (!Number.isFinite(shown) || shown < 0) shown = 0;
    if (shown > safeTotal) shown = safeTotal;

    return `${shown} of ${safeTotal}`;
  }

  static syncMobileFacetCount(documentSource = document, total = null) {
    const sourceCount = documentSource.querySelector('.mobile-facets__count');
    const targetCount = document.querySelector('.mobile-facets__count');

    if (sourceCount && targetCount) {
      targetCount.innerHTML = sourceCount.innerHTML;
      return;
    }

    const numericTotal = Number.parseInt(total, 10);
    if (targetCount && Number.isFinite(numericTotal)) {
      targetCount.textContent = `${numericTotal} product${numericTotal === 1 ? '' : 's'}`;
    }
  }

  static renderProductCount(parsedDocument) {
    const parsedProductCount = parsedDocument.getElementById('ProductCount');
    const parsedGridContainer = parsedDocument.getElementById('ProductGridContainer');
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');

    if (!container && !containerDesktop) return;

    let total = Number.parseInt(
      parsedProductCount?.getAttribute('data-total-results')
      || parsedGridContainer?.getAttribute('data-total-results')
      || '',
      10,
    );
    let pageSize = Number.parseInt(
      parsedProductCount?.getAttribute('data-products-per-page')
      || parsedGridContainer?.getAttribute('data-products-per-page')
      || '',
      10,
    );
    let currentPage = Number.parseInt(parsedProductCount?.getAttribute('data-current-page') || '', 10);
    const visibleCount = FacetFiltersForm.getVisibleProductItemCount();

    if (!Number.isFinite(total)) {
      const countText = parsedProductCount?.textContent || '';
      const countMatch = countText.match(/(?:^|\s)(\d+)\s+of\s+(\d+)(?:\s|$)/i);
      total = countMatch ? Number.parseInt(countMatch[2], 10) : visibleCount;
    }
    if (!Number.isFinite(pageSize) || pageSize <= 0) pageSize = visibleCount || total;
    if (!Number.isFinite(currentPage) || currentPage <= 0) currentPage = FacetFiltersForm.getCurrentPageFromURL();

    const count = FacetFiltersForm.formatShownOfTotal(total, pageSize, visibleCount, currentPage);

    [container, containerDesktop].forEach((target) => {
      if (!target) return;
      target.innerHTML = count;
      if (parsedProductCount) {
        Array.from(parsedProductCount.attributes).forEach((attribute) => {
          if (attribute.name.indexOf('data-') === 0) {
            target.setAttribute(attribute.name, attribute.value);
          }
        });
      }
      target.setAttribute('data-products-per-page', String(pageSize));
      target.setAttribute('data-current-page', String(currentPage));
      target.setAttribute('data-total-results', String(Number.isFinite(total) ? total : 0));
      target.setAttribute('data-count-text', count);
      target.classList.remove('loading');
    });

    FacetFiltersForm.syncMobileFacetCount(parsedDocument, total);

    if (window.cosmoshopUpdateProductCountProgress) {
      window.setTimeout(window.cosmoshopUpdateProductCountProgress, 0);
    }

    if (typeof initButtons === 'function') initButtons();
  }

  static renderFilters(parsedHTML, event) {
    const sourceMain = parsedHTML.querySelector('#FacetFiltersFormMobile .mobile-facets__main');
    const targetMain = document.querySelector('#FacetFiltersFormMobile .mobile-facets__main');

    if (sourceMain && targetMain) {
      const changedFilter = event && event.target && event.target.closest
        ? event.target.closest('.js-filter')
        : null;
      const changedIndex = changedFilter?.dataset?.index || '';
      const mainScrollTop = targetMain.scrollTop;
      const changedScrollContainer = changedFilter?.querySelector('.mobile-facets__submenu');
      const changedScrollTop = changedScrollContainer?.scrollTop || 0;
      const changedWasOpen = Boolean(changedFilter?.open);
      const openNestedFilters = changedFilter
        ? Array.from(changedFilter.querySelectorAll('details[open][data-cosmoshop-native-spec]'))
          .map((details) => details.getAttribute('data-cosmoshop-native-spec'))
        : [];

      // Replace stale options/counts with the current native Shopify facet response.
      // Retain the accordion's open and scroll state, not its old filter option nodes.
      targetMain.innerHTML = sourceMain.innerHTML;

      if (changedFilter && changedIndex) {
        const serverVersion = Array.from(targetMain.querySelectorAll('.js-filter'))
          .find((candidate) => candidate.dataset.index === changedIndex);
        if (serverVersion) {
          if (changedWasOpen) serverVersion.setAttribute('open', '');
          openNestedFilters.forEach((key) => {
            const nested = Array.from(serverVersion.querySelectorAll('[data-cosmoshop-native-spec]'))
              .find((candidate) => candidate.getAttribute('data-cosmoshop-native-spec') === key);
            if (nested) nested.setAttribute('open', '');
          });
          const restoredScrollContainer = serverVersion.querySelector('.mobile-facets__submenu');
          if (restoredScrollContainer) restoredScrollContainer.scrollTop = changedScrollTop;
        }
      }

      targetMain.scrollTop = mainScrollTop;
    }

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);
  }

  static renderActiveFacets(html) {
    const selectors = ['.active-facets-mobile', '.active-facets-desktop'];

    selectors.forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
      if (source.dataset.activeFilterCount !== undefined) {
        target.dataset.activeFilterCount = source.dataset.activeFilterCount;
      }
    });

    const sourceToolbarBottom = html.querySelector('.cosmoshop-filter-toolbar__bottom');
    const targetToolbarBottom = document.querySelector('.cosmoshop-filter-toolbar__bottom');
    if (sourceToolbarBottom && targetToolbarBottom) {
      targetToolbarBottom.innerHTML = sourceToolbarBottom.innerHTML;
      targetToolbarBottom.dataset.activeFilterCount = sourceToolbarBottom.dataset.activeFilterCount || '0';
    }
  }

  static renderAdditionalElements(html) {
    ['.mobile-facets__open', '.mobile-facets__count', '.sorting'].forEach((selector) => {
      const source = html.querySelector(selector);
      const target = document.querySelector(selector);
      if (!source || !target) return;
      target.innerHTML = source.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    const mobileDrawer = mobileForm ? mobileForm.closest('menu-drawer') : null;
    if (mobileDrawer && typeof mobileDrawer.bindEvents === 'function') mobileDrawer.bindEvents();
  }

  static renderCounts(source, target) {
    if (!source || !target) return;
    const sourceSelected = source.querySelector('.facets__selected');
    const targetSelected = target.querySelector('.facets__selected');
    if (sourceSelected && targetSelected) targetSelected.outerHTML = sourceSelected.outerHTML;
  }

  static collectionBasePath() {
    const match = window.location.pathname.match(/^(.*?\/collections\/[^/?#]+)/i);
    return match ? match[1] : window.location.pathname;
  }

  static preserveSearchContext(searchParams) {
    const params = new URLSearchParams(FacetFiltersForm.normaliseSearchParams(searchParams));
    if (window.location.pathname === '/search' || /\/search\/?$/i.test(window.location.pathname || '')) {
      const current = new URLSearchParams(window.location.search || '');
      const query = params.get('q') || current.get('q') || '';
      if (query) params.set('q', query);
      params.set('type', 'product');
      if (!params.has('options[prefix]')) params.set('options[prefix]', current.get('options[prefix]') || 'last');
    }
    return FacetFiltersForm.normaliseSearchParams(params);
  }

  static buildNavigationUrl(searchParams) {
    const query = FacetFiltersForm.preserveSearchContext(searchParams);
    const path = FacetFiltersForm.collectionBasePath();
    return `${path}${query ? `?${query}` : ''}`;
  }

  static buildSectionRequestUrl(sectionId, searchParams) {
    const params = new URLSearchParams(FacetFiltersForm.preserveSearchContext(searchParams));
    params.set('section_id', sectionId);

    // Do not convert tag filters into /tag-a+tag-b collection routes. Shopify treats
    // repeated values within one filter as OR, whereas a multi-tag collection route is
    // AND and therefore produces incorrect results for combinations such as two brands
    // or two demographic values.
    return `${FacetFiltersForm.collectionBasePath()}?${params.toString()}`;
  }

  static updateURLHash(searchParams) {
    const query = FacetFiltersForm.normaliseSearchParams(searchParams);
    const nextUrl = FacetFiltersForm.buildNavigationUrl(query);
    const currentUrl = `${window.location.pathname}${window.location.search}`;

    if (nextUrl === currentUrl) {
      history.replaceState({ searchParams: query }, '', nextUrl);
      return;
    }

    history.pushState({ searchParams: query }, '', nextUrl);
  }

  static scheduleAdjacentPagePrefetch(searchParams = window.location.search) {
    const run = () => FacetFiltersForm.prefetchAdjacentPages(searchParams);
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 900 });
    } else {
      window.setTimeout(run, 180);
    }
  }

  static prefetchAdjacentPages(searchParams = window.location.search) {
    if (
      typeof window.cosmoshopShouldUseClientGridForSearch === 'function'
      && window.cosmoshopShouldUseClientGridForSearch(searchParams)
    ) return;

    const sections = FacetFiltersForm.getSections();
    if (!sections.length) return;

    const wrapper = document.querySelector('#ProductGridContainer .pagination-wrapper');
    const totalPages = Number.parseInt(wrapper?.getAttribute('data-cosmoshop-pagination-pages') || '', 10);
    if (!Number.isFinite(totalPages) || totalPages <= 1) return;

    const base = new URLSearchParams(FacetFiltersForm.preserveSearchContext(searchParams));
    const currentPage = Math.max(1, Number.parseInt(base.get('page') || '1', 10) || 1);
    [currentPage - 1, currentPage + 1].forEach((page) => {
      if (page < 1 || page > totalPages) return;
      const params = new URLSearchParams(base.toString());
      params.set('page', String(page));
      sections.forEach((section) => {
        const url = FacetFiltersForm.buildSectionRequestUrl(section.section, params.toString());
        FacetFiltersForm.fetchSection(url, null).catch(() => {});
      });
    });
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    const productGridContainer = document.getElementById('ProductGridContainer');
    const sectionId = productGrid?.dataset?.id
      || productGridContainer?.dataset?.sectionId
      || document.querySelector('#main-collection-product-grid[data-id]')?.dataset?.id
      || document.querySelector('#main-collection-filters[data-id]')?.dataset?.id
      || '';
    return sectionId ? [{ section: sectionId }] : [];
  }

  onFormInput(event) {
    const target = event.target;
    if (!target || !target.matches) return;

    if (target.closest('price-range')) {
      FacetFiltersForm.scheduleFormRender(target.closest('form'), event, 350);
      return;
    }

    // Checkbox/radio/select changes are handled by the change listener so one user
    // action produces one render. Hidden sort inputs intentionally use input events.
    if (target.matches('input[type="checkbox"], input[type="radio"], select')) return;

    FacetFiltersForm.scheduleFormRender(target.closest('form'), event, 350);
  }

  onFormChange(event) {
    const target = event.target;
    if (!target || !target.matches) return;
    if (target.closest('price-range')) return;
    if (!target.matches('input[type="checkbox"], input[type="radio"], select')) return;

    const name = target.getAttribute('name') || '';
    if (!name.startsWith('filter.') && name !== 'sort_by') return;

    const searchParams = FacetFiltersForm.searchParamsFromChangedControl(target);
    FacetFiltersForm.scheduleFormRender(target.closest('form'), event, 100, searchParams);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    FacetFiltersForm.flushScheduledFormRender(event.target.closest('form'), event);
  }

  static searchParamsFromChangedControl(control) {
    const pending = FacetFiltersForm.pendingSearchParams;
    const baseSource = pending !== null && pending !== undefined
      ? pending
      : FacetFiltersForm.normaliseSearchParams(window.location.search);
    const params = new URLSearchParams(FacetFiltersForm.normaliseSearchParams(baseSource));

    params.delete('page');
    params.delete('sections');
    params.delete('section_id');

    const name = String(control?.getAttribute?.('name') || '');
    if (!name) return FacetFiltersForm.normaliseSearchParams(params);

    const value = String(control?.value == null ? '' : control.value).trim();
    const normaliseValue = (input) => String(input == null ? '' : input).trim().toLowerCase();

    if (control.matches('input[type="checkbox"]')) {
      const wanted = normaliseValue(value);
      const retained = params.getAll(name).filter((entry) => normaliseValue(entry) !== wanted);
      params.delete(name);
      retained.forEach((entry) => params.append(name, entry));
      if (control.checked && value !== '') params.append(name, value);
    } else if (control.matches('input[type="radio"]')) {
      params.delete(name);
      if (control.checked && value !== '') params.append(name, value);
    } else if (control.matches('select')) {
      params.delete(name);
      if (value !== '') params.append(name, value);
    }

    return FacetFiltersForm.normaliseSearchParams(params);
  }

  static cleanSearchParams(formData) {
    const params = new URLSearchParams();
    const seen = new Set();

    for (const [key, value] of formData.entries()) {
      if (key === 'page' || key === 'sections' || key === 'section_id') continue;

      const cleaned = typeof value === 'string' ? value.trim() : String(value == null ? '' : value);
      if ((key === 'filter.v.price.gte' || key === 'filter.v.price.lte') && cleaned === '') continue;
      if (cleaned === '' && key !== 'options[prefix]') continue;

      const signature = `${key}\u0000${cleaned}`;
      if (seen.has(signature)) continue;
      seen.add(signature);
      params.append(key, cleaned);
    }

    return FacetFiltersForm.normaliseSearchParams(params);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const href = event.currentTarget?.href || event.target?.closest?.('a')?.href || '';
    let searchParams = '';

    try {
      searchParams = new URL(href, window.location.href).searchParams.toString();
    } catch (error) {
      const queryIndex = String(href).indexOf('?');
      searchParams = queryIndex === -1 ? '' : String(href).slice(queryIndex + 1);
    }

    FacetFiltersForm.renderPage(searchParams, null);
  }
}

FacetFiltersForm.renderRequestId = 0;
FacetFiltersForm.renderAbortController = null;
FacetFiltersForm.pendingSubmitTimer = null;
FacetFiltersForm.pendingForm = null;
FacetFiltersForm.pendingEvent = null;
FacetFiltersForm.pendingSearchParams = null;
FacetFiltersForm.lastRequestedSearchParams = null;
FacetFiltersForm.sectionResponseCache = new Map();
FacetFiltersForm.sectionCacheMaxEntries = 36;
FacetFiltersForm.sectionCacheTtlMs = 3 * 60 * 1000;
FacetFiltersForm.searchParamsInitial = FacetFiltersForm.normaliseSearchParams(window.location.search);
FacetFiltersForm.searchParamsPrev = FacetFiltersForm.searchParamsInitial;
window.FacetFiltersForm = FacetFiltersForm;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

// Cosmoshop: collection/search pagination is AJAX-first. The URL still changes using
// history.pushState, but the browser shell, header, filters, CSS and JS stay in place.
// Exact section responses are cached, and adjacent native pages are prefetched.
document.addEventListener('click', (event) => {
  const link = event.target.closest && event.target.closest('#ProductGridContainer .pagination-wrapper a.pagination__item');
  if (!link) return;
  if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (link.target && link.target !== '_self') return;

  let target;
  try {
    target = new URL(link.href, window.location.href);
  } catch (error) {
    return;
  }
  if (target.origin !== window.location.origin) return;
  const current = `${window.location.pathname}${window.location.search || ''}`;
  const next = `${target.pathname}${target.search || ''}`;
  if (next === current) {
    event.preventDefault();
    event.stopImmediatePropagation();
    return;
  }

  event.preventDefault();
  event.stopImmediatePropagation();
  FacetFiltersForm.renderPage(target.searchParams.toString(), null, true, { paginationOnly: true });
}, true);

// Warm the next/previous native page after initial paint. Client-managed filtering has
// its own page cache/prefetcher and is automatically excluded by the helper above.
// Do not automatically fetch previous/next product-grid sections on page load.
// The filter drawer should not contend with background HTML page downloads.

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input')
      .forEach(element => element.addEventListener('change', this.onRangeChange.bind(this)));

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '') minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const link = this.querySelector('a');
    if (!link) return;

    link.addEventListener('click', (event) => {
      event.preventDefault();
      if (link.classList.contains('disabled') || link.getAttribute('aria-disabled') === 'true') return;
      const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
      if (form && typeof form.onActiveFilterClick === 'function') {
        form.onActiveFilterClick(event);
      } else {
        window.location.assign(link.href);
      }
    });
  }
}

customElements.define('facet-remove', FacetRemove);

// Cosmoshop: Apply commits the exact current form state, then closes the drawer.
// Automatic refresh and explicit Apply share the same debounced state machine, so
// the button can never submit stale checkboxes or create a duplicate request.
(() => {
  function closeMobileDrawer(form) {
    const disclosure = form ? form.closest('.mobile-facets__disclosure') : null;
    if (!disclosure || !disclosure.open) return;

    const summary = disclosure.querySelector(':scope > summary');
    if (summary) summary.click();
    else disclosure.removeAttribute('open');
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest && event.target.closest('#FacetFiltersFormMobile .mobile-facets__footer .button--primary');
    if (!button || String(button.getAttribute('type') || 'submit').toLowerCase() !== 'button') return;

    const form = button.closest('form') || document.getElementById('FacetFiltersFormMobile');
    if (!form || !window.FacetFiltersForm) return;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    window.FacetFiltersForm.flushScheduledFormRender(form, event);
    closeMobileDrawer(form);
  }, true);
})();


// Cosmoshop custom sort dropdown for the Filter & Sort drawer.
(() => {
  const selector = '[data-cosmoshop-sort-select]';

  function closeSort(root) {
    if (!root) return;
    root.classList.remove('is-open');
    const button = root.querySelector('.cosmoshop-sort-select__button');
    if (button) button.setAttribute('aria-expanded', 'false');
  }

  function openSort(root) {
    if (!root) return;
    closeAllSorts(root);
    root.classList.add('is-open');
    const button = root.querySelector('.cosmoshop-sort-select__button');
    if (button) button.setAttribute('aria-expanded', 'true');
  }

  function closeAllSorts(except) {
    document.querySelectorAll(selector).forEach((root) => {
      if (root !== except) closeSort(root);
    });
  }

  // Use capture so these controls never reach Shopify's menu-drawer "any button closes" handler.
  document.addEventListener('click', (event) => {
    const button = event.target.closest('.cosmoshop-sort-select__button');
    const option = event.target.closest('.cosmoshop-sort-select__option');
    const rootFromClick = event.target.closest(selector);

    if (button) {
      const root = button.closest(selector);
      const isOpen = root.classList.contains('is-open');
      if (isOpen) {
        closeSort(root);
      } else {
        openSort(root);
      }
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (option) {
      const root = option.closest(selector);
      const hiddenInput = root.querySelector('[data-cosmoshop-sort-input]');
      const valueLabel = root.querySelector('.cosmoshop-sort-select__value');
      const nextValue = option.dataset.value || '';
      const nextLabel = option.textContent.trim();

      root.querySelectorAll('.cosmoshop-sort-select__option').forEach((item) => {
        const selected = item === option;
        item.classList.toggle('is-selected', selected);
        item.setAttribute('aria-selected', selected ? 'true' : 'false');
      });

      if (valueLabel) valueLabel.textContent = nextLabel;

      if (hiddenInput && hiddenInput.value !== nextValue) {
        hiddenInput.value = nextValue;
        hiddenInput.dispatchEvent(new Event('input', { bubbles: true }));
      }

      closeSort(root);
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!rootFromClick) closeAllSorts();
  }, true);

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeAllSorts();
  });
})();


/* Cosmoshop live filter option counts + fallback specification counts. */
(() => {
  const READY_SPEC_DATA = {"invicta-nhl-philadelphia-flyers-unisex-quartz-watch-42215":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["38"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Flame Fusion"],"filter.p.m.custom.water_resistance_m":["100"],"filter.p.m.custom.dial_color":["Silver, Black, White"],"filter.p.m.custom.target_gender":["Unisex"]},"invicta-nhl-pittsburgh-penguins-orange-unisex-watch-42208":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["38"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Flame Fusion"],"filter.p.m.custom.water_resistance_m":["100"],"filter.p.m.custom.dial_color":["Yellow, Orange, Black"],"filter.p.m.custom.target_gender":["Unisex"]},"invicta-nhl-minnesota-wild-unisex-quartz-watch-in42214":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["38"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Flame Fusion"],"filter.p.m.custom.water_resistance_m":["100"],"filter.p.m.custom.dial_color":["Yellow, Silver, White"],"filter.p.m.custom.target_gender":["Unisex"]},"invicta-pro-diver-green-bezel-silicone-strap-mens-watch-46129":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["45"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Silicone"],"filter.p.m.custom.strap_material":["Silicone"],"filter.p.m.custom.crystal_material":["Flame Fusion"],"filter.p.m.custom.water_resistance_m":["50"],"filter.p.m.custom.dial_color":["Green"],"filter.p.m.custom.target_gender":["Men"]},"invicta-pro-diver-blue-bezel-silicone-strap-mens-watch-in46130":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["45"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Silicone"],"filter.p.m.custom.strap_material":["Silicone"],"filter.p.m.custom.crystal_material":["Flame Fusion"],"filter.p.m.custom.water_resistance_m":["50"],"filter.p.m.custom.dial_color":["Black, Blue"],"filter.p.m.custom.target_gender":["Men"]},"citizen-tsuyosa-60-automatic-blue-dial-mens-watch-nk0023-57l":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["40"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Anti-Reflective Sapphire"],"filter.p.m.custom.water_resistance_m":["50"],"filter.p.m.custom.dial_color":["Blue"],"filter.p.m.custom.clasp_type":["Push Button Clasp"],"filter.p.m.custom.target_gender":["Men"]},"citizen-tsuyosa-shore-blue-dial-automatic-watch-nj0230-59l":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["40"],"filter.p.m.custom.case_thickness_mm":["11.7"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["100"],"filter.p.m.custom.dial_color":["Blue"],"filter.p.m.custom.clasp_type":["Push Button Clasp"],"filter.p.m.custom.target_gender":["Men"]},"citizen-promaster-sky-radio-controlled-mens-watch-at8300-58e":{"filter.p.m.custom.watch_movement":["Eco-Drive"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["41"],"filter.p.m.custom.case_thickness_mm":["11.62"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Anti-Reflective Sapphire"],"filter.p.m.custom.water_resistance_m":["200"],"filter.p.m.custom.dial_color":["Black"],"filter.p.m.custom.clasp_type":["Push Button Clasp"],"filter.p.m.custom.target_gender":["Men"]},"bulova-malachite-goddess-of-time-gold-ladies-watch-97l184":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["17"],"filter.p.m.custom.case_thickness_mm":["7.4"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel Mesh"],"filter.p.m.custom.strap_material":["Stainless Steel Mesh"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["30"],"filter.p.m.custom.dial_color":["Malachite, Green, Gold"],"filter.p.m.custom.clasp_type":["Sliding Clasp"],"filter.p.m.custom.target_gender":["Women"]},"bulova-lunar-pilot-special-edition-43-5mm-chronograph-watch-96k111":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["43.5"],"filter.p.m.custom.case_thickness_mm":["13.21"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["50"],"filter.p.m.custom.dial_color":["Black, White"],"filter.p.m.custom.clasp_type":["Deployment Clasp"],"filter.p.m.custom.target_gender":["Men"]},"citizen-mechanical-leather-strap-automatic-watch-nj0140-17e":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["42"],"filter.p.m.custom.case_thickness_mm":["13"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Leather"],"filter.p.m.custom.strap_material":["Leather"],"filter.p.m.custom.crystal_material":["Glass"],"filter.p.m.custom.water_resistance_m":["100"],"filter.p.m.custom.target_gender":["Men"]},"bulova-aerojet-open-heart-leather-strap-automatic-mens-watch-98a289":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["41"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Leather"],"filter.p.m.custom.strap_material":["Leather"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["30"],"filter.p.m.custom.dial_color":["Rose Gold, Silver, Gold"],"filter.p.m.custom.target_gender":["Men"]},"bulova-open-heart-aerojet-automatic-blue-dial-gents-watch-96a277":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["41"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["30"],"filter.p.m.custom.dial_color":["Blue"],"filter.p.m.custom.clasp_type":["Deployment Clasp"],"filter.p.m.custom.target_gender":["Men"]},"invicta-specialty-compass-mens-watch-black-in35679":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["45"],"filter.p.m.custom.case_thickness_mm":["12"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Silicone"],"filter.p.m.custom.strap_material":["Silicone"],"filter.p.m.custom.crystal_material":["Flame Fusion"],"filter.p.m.custom.water_resistance_m":["100"],"filter.p.m.custom.dial_color":["Black"],"filter.p.m.custom.clasp_type":["Buckle"],"filter.p.m.custom.target_gender":["Men"]},"invicta-pro-diver-silver-black-dial-mens-watch-34022":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["40"],"filter.p.m.custom.case_thickness_mm":["12"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Flame Fusion"],"filter.p.m.custom.water_resistance_m":["200"],"filter.p.m.custom.dial_color":["Silver, Black"],"filter.p.m.custom.clasp_type":["Fold Over Clasp"],"filter.p.m.custom.target_gender":["Men"]},"boccia-titanium-square-gold-tan-ladies-watch-3334-03":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["33"],"filter.p.m.custom.case_material":["Titanium"],"filter.p.m.custom.band_material":["Leather"],"filter.p.m.custom.strap_material":["Leather"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["30"],"filter.p.m.custom.dial_color":["Gold"],"filter.p.m.custom.target_gender":["Women"]},"bulova-series-x-precisionist-special-edition-gents-watch-98b429":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["45"],"filter.p.m.custom.case_thickness_mm":["17.6"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["50"],"filter.p.m.custom.dial_color":["Black, Gold"],"filter.p.m.custom.clasp_type":["Deployment Clasp"],"filter.p.m.custom.target_gender":["Men"]},"bulova-jet-star-series-262hz-precisionist-mens-watch-96b472":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["39"],"filter.p.m.custom.case_thickness_mm":["12.5"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["30"],"filter.p.m.custom.dial_color":["Silver, Grey"],"filter.p.m.custom.clasp_type":["Deployment Clasp"],"filter.p.m.custom.target_gender":["Men"]},"bulova-classic-surveyor-automatic-green-dial-mens-watch-96b427":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["39"],"filter.p.m.custom.case_thickness_mm":["11.7"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Leather"],"filter.p.m.custom.strap_material":["Leather"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.dial_color":["Silver, Green"],"filter.p.m.custom.target_gender":["Men"]},"bulova-duo-tone-surveyor-diamond-markers-ladies-watch-98p207":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["31"],"filter.p.m.custom.case_thickness_mm":["7.7"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["30"],"filter.p.m.custom.dial_color":["Mother Of Pearl, White"],"filter.p.m.custom.target_gender":["Women"]},"citizen-limited-edition-promaster-fugu-gents-watch-ny0161-63l":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["42"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["200"],"filter.p.m.custom.dial_color":["Blue"],"filter.p.m.custom.clasp_type":["Fold Over Clasp"],"filter.p.m.custom.target_gender":["Men"]},"citizen-tsuyosa-textured-blue-dial-automatic-gents-watch-nj0151-88l":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["40"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["50"],"filter.p.m.custom.dial_color":["Blue, Gold, Navy"],"filter.p.m.custom.clasp_type":["Push Button Clasp"],"filter.p.m.custom.target_gender":["Men"]},"invicta-specialty-gold-dial-mens-quartz-watch-48970":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["45"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["100"],"filter.p.m.custom.dial_color":["Gold"],"filter.p.m.custom.target_gender":["Men"]},"invicta-racing-tiffany-blue-dial-oversized-mens-watch-47202":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["48"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel Mesh"],"filter.p.m.custom.strap_material":["Stainless Steel Mesh"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["30"],"filter.p.m.custom.dial_color":["Blue"],"filter.p.m.custom.target_gender":["Men"]},"invicta-speedway-green-dial-chronograph-mens-watch-49380":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["44"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["50"],"filter.p.m.custom.dial_color":["Green"],"filter.p.m.custom.target_gender":["Men"]},"adidas-retro-wave-two-dark-blue-unisex-watch-aosy24029":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["37"],"filter.p.m.custom.case_material":["Ceramic"],"filter.p.m.custom.band_material":["Resin"],"filter.p.m.custom.strap_material":["Resin"],"filter.p.m.custom.water_resistance_m":["50"],"filter.p.m.custom.dial_color":["Dark Blue, Blue"],"filter.p.m.custom.target_gender":["Unisex"]},"adidas-project-three-white-pu-unisex-watch-aost24548":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["42"],"filter.p.m.custom.case_thickness_mm":["11"],"filter.p.m.custom.case_material":["Polyurethane, Plastic"],"filter.p.m.custom.band_material":["Polyurethane, Plastic"],"filter.p.m.custom.strap_material":["Polyurethane, Plastic"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["50"],"filter.p.m.custom.dial_color":["White"],"filter.p.m.custom.target_gender":["Unisex"]},"adidas-project-three-black-pu-unisex-watch-aost24547":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["42"],"filter.p.m.custom.case_material":["Polyurethane, Plastic"],"filter.p.m.custom.band_material":["Polyurethane, Plastic"],"filter.p.m.custom.strap_material":["Polyurethane, Plastic"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["50"],"filter.p.m.custom.dial_color":["Black"],"filter.p.m.custom.target_gender":["Unisex"]},"boccia-titanium-green-dial-leather-strap-unisex-watch-3648-03":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["39"],"filter.p.m.custom.case_material":["Titanium"],"filter.p.m.custom.band_material":["Leather"],"filter.p.m.custom.strap_material":["Leather"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["50"],"filter.p.m.custom.dial_color":["Green"],"filter.p.m.custom.target_gender":["Unisex"]},"citizen-promaster-yellow-dial-automatic-mens-divers-watch-ny0120-52x":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["41"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["200"],"filter.p.m.custom.dial_color":["Yellow"],"filter.p.m.custom.clasp_type":["Push Button Clasp"],"filter.p.m.custom.target_gender":["Men"]},"citizen-sports-automatic-light-blue-dial-mens-watch-nh7530-52l":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["42"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["100"],"filter.p.m.custom.dial_color":["Light Blue, Blue"],"filter.p.m.custom.clasp_type":["Push Button Clasp"],"filter.p.m.custom.target_gender":["Men"]},"boccia-titanium-mop-dial-rose-gold-ladies-watch-3266-03":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["27"],"filter.p.m.custom.case_material":["Titanium"],"filter.p.m.custom.band_material":["Leather"],"filter.p.m.custom.strap_material":["Leather"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["30"],"filter.p.m.custom.dial_color":["Mother Of Pearl, Rose Gold, Gold"],"filter.p.m.custom.target_gender":["Women"]},"citizen-classic-chronograph-blue-dial-mens-watch-ca0748-80l":{"filter.p.m.custom.watch_movement":["Eco-Drive"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["43"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["100"],"filter.p.m.custom.dial_color":["Blue"],"filter.p.m.custom.clasp_type":["Push Button Clasp"],"filter.p.m.custom.target_gender":["Men"]},"citizen-classic-blue-dial-eco-drive-mens-watch-bm7631-87m":{"filter.p.m.custom.watch_movement":["Eco-Drive"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["41"],"filter.p.m.custom.case_thickness_mm":["9.7"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["100"],"filter.p.m.custom.dial_color":["Turquoise, Blue"],"filter.p.m.custom.target_gender":["Men"]},"citizen-promaster-land-gmt-blue-dial-eco-drive-mens-watch-bj7150-09l":{"filter.p.m.custom.watch_movement":["Eco-Drive"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["39.5"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Nylon"],"filter.p.m.custom.strap_material":["Nylon"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["200"],"filter.p.m.custom.dial_color":["Orange, White, Blue"],"filter.p.m.custom.clasp_type":["Buckle"],"filter.p.m.custom.target_gender":["Men"]},"citizen-classic-sport-eco-drive-black-dial-mens-watch-aw1760-81e":{"filter.p.m.custom.watch_movement":["Eco-Drive"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["43"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["100"],"filter.p.m.custom.dial_color":["Black"],"filter.p.m.custom.target_gender":["Men"]},"citizen-classic-sport-eco-drive-red-black-bezel-mens-watch-aw1769-10e":{"filter.p.m.custom.watch_movement":["Eco-Drive"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["43"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Urethane Strap, Buckle"],"filter.p.m.custom.strap_material":["Urethane Strap, Buckle"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["100"],"filter.p.m.custom.dial_color":["Black, Red"],"filter.p.m.custom.clasp_type":["Buckle"],"filter.p.m.custom.target_gender":["Men"]},"citizen-tsuno-bullhead-red-dial-unisex-chronograph-watch-an3700-89z":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["38"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["50"],"filter.p.m.custom.dial_color":["Orange, Red"],"filter.p.m.custom.clasp_type":["Push Button Clasp"],"filter.p.m.custom.target_gender":["Unisex"]},"citizen-tsuno-classic-unisex-chronograph-watch-an3660-73x":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["38"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["50"],"filter.p.m.custom.dial_color":["Green"],"filter.p.m.custom.clasp_type":["Push Button Clasp"],"filter.p.m.custom.target_gender":["Unisex"]},"citizen-promaster-marine-black-dial-eco-drive-mens-watch-bn0261-51e":{"filter.p.m.custom.watch_movement":["Eco-Drive"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["40.55"],"filter.p.m.custom.case_thickness_mm":["11.69"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Anti-Reflective Sapphire"],"filter.p.m.custom.water_resistance_m":["200"],"filter.p.m.custom.dial_color":["Black"],"filter.p.m.custom.clasp_type":["Fold Over Clasp"],"filter.p.m.custom.target_gender":["Men"]},"citizen-promaster-marine-blue-dial-eco-drive-mens-watch-bn0165-55l":{"filter.p.m.custom.watch_movement":["Eco-Drive"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["45"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["200"],"filter.p.m.custom.dial_color":["Blue"],"filter.p.m.custom.clasp_type":["Push Button Clasp"],"filter.p.m.custom.target_gender":["Men"]},"bulova-marine-star-series-b-green-dial-automatic-mens-watch-98b471":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["41"],"filter.p.m.custom.case_thickness_mm":["12.8"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["200"],"filter.p.m.custom.dial_color":["Green"],"filter.p.m.custom.target_gender":["Men"]},"bulova-marine-star-series-b-two-tone-mens-watch-98b467":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["41"],"filter.p.m.custom.case_thickness_mm":["12.8"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["200"],"filter.p.m.custom.dial_color":["Two Tone, Two-Tone, Green"],"filter.p.m.custom.target_gender":["Men"]},"bulova-marine-star-series-b-automatic-mens-watch-98b466":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["41"],"filter.p.m.custom.case_thickness_mm":["12.8"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["200"],"filter.p.m.custom.dial_color":["Black, Blue"],"filter.p.m.custom.clasp_type":["Fold Over Clasp"],"filter.p.m.custom.target_gender":["Men"]},"citizen-tsuyosa-60-two-tone-green-dial-mens-automatic-watch-nk0024-54x":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["40"],"filter.p.m.custom.case_thickness_mm":["11.7"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Anti-Reflective Sapphire"],"filter.p.m.custom.water_resistance_m":["50"],"filter.p.m.custom.dial_color":["Two Tone, Two-Tone, Green"],"filter.p.m.custom.clasp_type":["Push Button Clasp"],"filter.p.m.custom.target_gender":["Men"]},"bulova-super-seville-two-tone-38mm-mens-watch-98b458":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["38"],"filter.p.m.custom.case_thickness_mm":["10.3"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Anti-Reflective Sapphire"],"filter.p.m.custom.water_resistance_m":["30"],"filter.p.m.custom.dial_color":["Two Tone, Two-Tone, Black"],"filter.p.m.custom.clasp_type":["Deployment Clasp"],"filter.p.m.custom.target_gender":["Men"]},"bulova-sutton-square-mop-dial-elegant-ladies-watch-96l357":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["23"],"filter.p.m.custom.case_thickness_mm":["6.6"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["30"],"filter.p.m.custom.dial_color":["Mother Of Pearl, White, MOP"],"filter.p.m.custom.target_gender":["Women"]},"bulova-super-seville-mini-mop-dial-ladies-watch-96l353":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["25"],"filter.p.m.custom.case_thickness_mm":["7.6"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["30"],"filter.p.m.custom.dial_color":["Mother Of Pearl, White, MOP"],"filter.p.m.custom.clasp_type":["Deployment Clasp"],"filter.p.m.custom.target_gender":["Women"]},"bulova-marine-star-ladies-mop-dial-automatic-watch-96l326":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["35"],"filter.p.m.custom.case_thickness_mm":["9.7"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["100"],"filter.p.m.custom.dial_color":["Mother Of Pearl, MOP"],"filter.p.m.custom.clasp_type":["Deployment Clasp"],"filter.p.m.custom.target_gender":["Women"]},"citizen-automatic-series8-880-gmt-mens-watch-nb6034-58l":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["41"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["100"],"filter.p.m.custom.dial_color":["Orange, Black"],"filter.p.m.custom.clasp_type":["Push Button Clasp"],"filter.p.m.custom.target_gender":["Men"]},"citizen-automatic-series8-880-gmt-mens-watch-nb6033-51e":{"filter.p.m.custom.watch_movement":["Automatic"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["41"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Sapphire"],"filter.p.m.custom.water_resistance_m":["100"],"filter.p.m.custom.dial_color":["Orange, Black"],"filter.p.m.custom.clasp_type":["Push Button Clasp"],"filter.p.m.custom.target_gender":["Men"]},"invicta-oversized-tiffany-blue-mens-watch-48012":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["49.5"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel Mesh"],"filter.p.m.custom.strap_material":["Stainless Steel Mesh"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["50"],"filter.p.m.custom.dial_color":["Blue"],"filter.p.m.custom.target_gender":["Men"]},"invicta-pro-diver-gold-unisex-watch-black-bezel-48728":{"filter.p.m.custom.watch_movement":["Quartz"],"filter.p.m.custom.watch_display":["Analog"],"filter.p.m.custom.case_diameter_mm":["37.5"],"filter.p.m.custom.case_material":["Stainless Steel"],"filter.p.m.custom.band_material":["Stainless Steel"],"filter.p.m.custom.strap_material":["Stainless Steel"],"filter.p.m.custom.crystal_material":["Mineral"],"filter.p.m.custom.water_resistance_m":["200"],"filter.p.m.custom.dial_color":["Black, Gold"],"filter.p.m.custom.target_gender":["Unisex"]}};
  const COLLECTION_CACHE = new Map();
  const MAX_COLLECTION_PRODUCT_JSON_PAGES = 20;
  const MAX_PARALLEL_PRODUCT_JSON_FETCHES = 4;
  const SPEC_PARAMS = new Set([
    'filter.p.m.custom.watch_movement',
    'filter.p.m.custom.watch_display',
    'filter.p.m.custom.case_diameter_mm',
    'filter.p.m.custom.case_thickness_mm',
    'filter.p.m.custom.case_material',
    'filter.p.m.custom.band_material',
    'filter.p.m.custom.strap_material',
    'filter.p.m.custom.crystal_material',
    'filter.p.m.custom.water_resistance_m',
    'filter.p.m.custom.dial_color',
    'filter.p.m.custom.target_gender',
    'filter.p.m.custom.clasp_type'
  ]);
  const LIVE_COUNT_PARAMS = new Set([
    'filter.v.availability',
    'filter.p.vendor',
    'filter.p.t.category',
    'filter.p.category',
    'filter.p.product_type',
    'filter.p.tag',
    ...SPEC_PARAMS
  ]);

  function normalise(value) {
    return String(value || '')
      .toLowerCase()
      .replace(/[’']/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9.]+/g, ' ')
      .trim();
  }

  function sameValue(a, b) {
    const na = normalise(a);
    const nb = normalise(b);
    if (!na || !nb) return false;
    if (na === nb) return true;
    if (na.length > 3 && nb.length > 3 && na.replace(/s$/, '') === nb.replace(/s$/, '')) return true;
    const aliases = {
      mens: ['men', 'mens', 'male'],
      men: ['men', 'mens', 'male'],
      male: ['men', 'mens', 'male'],
      womens: ['women', 'womens', 'female'],
      women: ['women', 'womens', 'female'],
      female: ['women', 'womens', 'female'],
      preorder: ['pre order', 'preorder'],
      'pre order': ['pre order', 'preorder'],
      'final price': ['final price', 'finalprice'],
      finalprice: ['final price', 'finalprice'],
      lq: ['lq', 'limited quantity', 'limitedquantity', 'limited availability', 'limitedavailability', 'limited-availability'],
      'limited quantity': ['lq', 'limited quantity', 'limitedquantity', 'limited availability', 'limitedavailability', 'limited-availability'],
      'limited availability': ['lq', 'limited quantity', 'limitedquantity', 'limited availability', 'limitedavailability', 'limited-availability'],
      limitedavailability: ['lq', 'limited quantity', 'limitedquantity', 'limited availability', 'limitedavailability', 'limited-availability'],
      'limited-availability': ['lq', 'limited quantity', 'limitedquantity', 'limited availability', 'limitedavailability', 'limited-availability'],
      limitedquantity: ['lq', 'limited quantity', 'limitedquantity', 'limited availability', 'limitedavailability', 'limited-availability'],
      available: ['available', 'in stock', 'instock', '1', 'true'],
      instock: ['available', 'in stock', 'instock', '1', 'true'],
      unavailable: ['unavailable', 'out of stock', 'outofstock', '0', 'false'],
      outofstock: ['unavailable', 'out of stock', 'outofstock', '0', 'false']
    };
    return Boolean((aliases[na] && aliases[na].includes(nb)) || (aliases[nb] && aliases[nb].includes(na)));
  }

  function withSpecificationUnit(param, value) {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (!text) return text;
    const lower = text.toLowerCase();
    if ((param === 'filter.p.m.custom.case_diameter_mm' || param === 'filter.p.m.custom.case_thickness_mm') && !lower.includes('mm')) {
      return `${text} mm`;
    }
    if (param === 'filter.p.m.custom.water_resistance_m' && !lower.includes('m')) {
      return `${text} m`;
    }
    return text;
  }

  function prettyFallbackLabel(param, value) {
    const n = normalise(value);
    if (param === 'filter.p.tag') {
      if (n === 'mens' || n === 'men' || n === 'male') return "Men's";
      if (n === 'womens' || n === 'women' || n === 'female') return "Women's";
      if (n === 'unisex') return 'Unisex';
      if (n === 'kids' || n === 'kid') return 'Kids';
      if (n === 'pre order' || n === 'preorder') return 'Pre-order';
      if (n === 'final price' || n === 'finalprice') return 'Final price';
      if (n === 'lq' || n === 'limited quantity' || n === 'limitedquantity' || n === 'limited availability' || n === 'limitedavailability' || n === 'limited-availability') return 'Limited availability';
      if (n === 'ex demo' || n === 'exdemo') return 'Ex-Demo';
      if (n === 'clearance') return 'Clearance';
    }
    return withSpecificationUnit(param, value);
  }

  function collectionHandleFromPath() {
    const match = window.location.pathname.match(/\/collections\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : '';
  }

  function collectionProductsJsonUrl(handle, page, sortBy = '') {
    const root = window.Shopify?.routes?.root || '/';
    const params = new URLSearchParams({ limit: '250', page: String(page) });
    if (sortBy) params.set('sort_by', sortBy);
    return `${root}collections/${encodeURIComponent(handle)}/products.json?${params.toString()}`;
  }

  async function fetchCollectionProductJsonPage(handle, page, sortBy = '') {
    const response = await fetch(collectionProductsJsonUrl(handle, page, sortBy), { credentials: 'same-origin' });
    if (!response.ok) return [];
    const payload = await response.json();
    return Array.isArray(payload.products) ? payload.products : [];
  }

  async function fetchCollectionProducts(handle, sortBy = '') {
    if (!handle) return [];
    const cacheKey = `${handle}::${sortBy || ''}`;
    if (COLLECTION_CACHE.has(cacheKey)) return COLLECTION_CACHE.get(cacheKey);

    const promise = (async () => {
      const products = [];
      const firstBatch = await fetchCollectionProductJsonPage(handle, 1, sortBy);
      products.push(...firstBatch);

      // The previous implementation fetched each 250-product JSON page serially.
      // Large collections therefore waited for five or more full network round trips
      // before filtering could even begin. Fetch the remaining pages in small parallel
      // batches and stop as soon as Shopify returns a short page.
      if (firstBatch.length >= 250) {
        for (let startPage = 2; startPage <= MAX_COLLECTION_PRODUCT_JSON_PAGES; startPage += MAX_PARALLEL_PRODUCT_JSON_FETCHES) {
          const pageNumbers = [];
          for (let page = startPage; page < startPage + MAX_PARALLEL_PRODUCT_JSON_FETCHES && page <= MAX_COLLECTION_PRODUCT_JSON_PAGES; page += 1) {
            pageNumbers.push(page);
          }

          const pageResults = await Promise.all(pageNumbers.map(async (page) => ({
            page,
            products: await fetchCollectionProductJsonPage(handle, page, sortBy).catch(() => []),
          })));

          let reachedEnd = false;
          pageResults
            .sort((a, b) => a.page - b.page)
            .forEach(({ products: batch }) => {
              products.push(...batch);
              if (batch.length < 250) reachedEnd = true;
            });

          if (reachedEnd) break;
        }
      }

      const uniqueProducts = new Map();
      products.forEach((product) => {
        const key = String(product.id || product.handle || product.title || '').trim();
        if (!key || uniqueProducts.has(key)) return;
        uniqueProducts.set(key, product);
      });

      return Array.from(uniqueProducts.values()).map((product, sourceIndex) => {
        const variants = Array.isArray(product.variants) ? product.variants : [];
        const tags = Array.isArray(product.tags) ? product.tags : String(product.tags || '').split(',').map((tag) => tag.trim()).filter(Boolean);
        const prices = variants.map((variant) => Number(variant.price)).filter((price) => Number.isFinite(price));
        const savings = variants.map((variant) => {
          const price = Number(variant.price);
          const compareAtPrice = Number(variant.compare_at_price);
          if (!Number.isFinite(price) || !Number.isFinite(compareAtPrice) || compareAtPrice <= price) return 0;
          return Math.floor(((compareAtPrice - price) * 100) / compareAtPrice);
        }).filter((saving) => Number.isFinite(saving));
        return {
          id: product.id || '',
          handle: product.handle || '',
          title: product.title || '',
          vendor: product.vendor || '',
          productType: product.product_type || product.type || '',
          tags,
          available: variants.length ? variants.some((variant) => variant.available) : Boolean(product.available),
          minPrice: prices.length ? Math.min(...prices) : 0,
          largestSaving: savings.length ? Math.max(...savings) : 0,
          specs: READY_SPEC_DATA[product.handle] || {},
          sourceIndex,
        };
      });
    })().catch((error) => {
      COLLECTION_CACHE.delete(cacheKey);
      throw error;
    });

    COLLECTION_CACHE.set(cacheKey, promise);
    return promise;
  }

  function valuesForParam(product, param) {
    if (!product) return [];
    if (param === 'filter.v.availability') {
      return product.available ? ['1', 'true', 'available', 'in stock', 'instock'] : ['0', 'false', 'unavailable', 'out of stock', 'outofstock'];
    }
    if (param === 'filter.p.vendor') return [product.vendor];
    if (param === 'filter.p.product_type') return [product.productType];
    if (param === 'filter.p.tag') {
      const out = [...product.tags];
      const specGender = product.specs['filter.p.m.custom.target_gender'];
      if (Array.isArray(specGender)) out.push(...specGender);
      product.tags.forEach((tag) => {
        const n = normalise(tag);
        if (n === 'mens' || n === 'men' || n === 'male') out.push("Men's", 'mens', 'Men', 'Male');
        if (n === 'womens' || n === 'women' || n === 'female') out.push("Women's", 'womens', 'Women', 'Female');
        if (n === 'unisex') out.push('Unisex', 'unisex');
        if (n === 'kids' || n === 'kid') out.push('Kids', 'kids');
        if (n === 'pre order' || n === 'preorder') out.push('Pre-order', 'Pre-order', 'pre-order');
        if (n === 'final price' || n === 'finalprice') out.push('Final price', 'Final Price', 'final-price');
        if (n === 'lq' || n === 'limited quantity' || n === 'limitedquantity' || n === 'limited availability' || n === 'limitedavailability' || n === 'limited-availability') out.push('Limited availability', 'Limited Availability', 'limited-availability', 'limited-quantity', 'lq');
        if (n === 'ex demo' || n === 'exdemo') out.push('Ex-Demo', 'Ex-demo', 'ex-demo');
        if (n === 'clearance') out.push('Clearance', 'clearance');
      });
      return out;
    }
    if (SPEC_PARAMS.has(param)) return product.specs[param] || [];
    return [];
  }

  function productHasValue(product, param, value) {
    return valuesForParam(product, param).some((candidate) => sameValue(candidate, value));
  }

  function canonicalPromotionTagGroup(value) {
    const normalized = normalise(value);
    if (['pre order', 'preorder', 'new promotion pre order'].includes(normalized)) return 'promotion';
    if (['final price', 'finalprice', 'new promotion final price'].includes(normalized)) return 'promotion';
    if (['ex demo', 'exdemo', 'new promotion ex demo'].includes(normalized)) return 'promotion';
    if (['lq', 'limited quantity', 'limitedquantity', 'limited availability', 'limitedavailability', 'limited-availability', 'new promotion limited quantity', 'new promotion limited availability'].includes(normalized)) return 'promotion';
    return '';
  }

  function tagFilterGroup(value) {
    const raw = String(value == null ? '' : value).trim().toLowerCase();
    const separatorIndex = raw.indexOf(' - ');
    if (raw.startsWith('new-') && separatorIndex > 4) {
      return raw.slice(4, separatorIndex).replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim();
    }

    const promotionGroup = canonicalPromotionTagGroup(raw);
    if (promotionGroup) return promotionGroup;
    return 'other tag';
  }

  function productMatchesTagGroups(product, values) {
    // OR within each logical tag-backed facet, AND across the facet groups.
    // Example: (Automatic OR Eco-Drive) AND (Analog OR Digital) AND (Men OR Unisex).
    const groupedValues = new Map();
    values.forEach((value) => {
      const group = tagFilterGroup(value);
      if (!groupedValues.has(group)) groupedValues.set(group, []);
      groupedValues.get(group).push(value);
    });

    for (const groupValues of groupedValues.values()) {
      if (!groupValues.some((value) => productHasValue(product, 'filter.p.tag', value))) return false;
    }
    return true;
  }

  function selectedFilters() {
    const params = new URLSearchParams(window.location.search);
    const filters = new Map();
    params.forEach((value, key) => {
      if (!key.startsWith('filter.')) return;
      if (!filters.has(key)) filters.set(key, []);
      filters.get(key).push(value);
    });
    return filters;
  }

  function productMatches(product, filters, excludedParam) {
    for (const [param, values] of filters.entries()) {
      if (param === excludedParam) continue;
      if (param === 'filter.v.price.gte') {
        const min = Number(values[values.length - 1]);
        if (Number.isFinite(min) && product.minPrice < min) return false;
        continue;
      }
      if (param === 'filter.v.price.lte') {
        const max = Number(values[values.length - 1]);
        if (Number.isFinite(max) && product.minPrice > max) return false;
        continue;
      }
      if (param === 'filter.p.tag') {
        if (!productMatchesTagGroups(product, values)) return false;
        continue;
      }
      const matchesAny = values.some((value) => productHasValue(product, param, value));
      if (!matchesAny) return false;
    }
    return true;
  }

  function optionIsActive(param, value, filters) {
    const values = filters.get(param) || [];
    return values.some((selected) => sameValue(selected, value));
  }

  // Shopify's normal facet semantics are:
  //   - AND between different filter dimensions
  //   - OR between multiple selected values within one dimension
  //
  // Cosmoshop stores several logical dimensions inside filter.p.tag (Product Type,
  // Gender, Age, Promotions and tag-backed Specifications). Treat each new-<field>
  // prefix as its own dimension so those groups AND together rather than collapsing
  // into one global tag OR bucket.
  function filterDimensionKey(param, value) {
    if (param === 'filter.p.tag') return `${param}:${tagFilterGroup(value)}`;
    if (param === 'filter.v.price.gte' || param === 'filter.v.price.lte') return 'filter.v.price';
    return param;
  }

  function filtersWithoutDimension(filters, param, value) {
    const targetDimension = filterDimensionKey(param, value);
    const next = new Map();

    for (const [existingParam, existingValues] of filters.entries()) {
      const keptValues = existingValues.filter(
        (existingValue) => filterDimensionKey(existingParam, existingValue) !== targetDimension
      );
      if (keptValues.length) next.set(existingParam, keptValues);
    }

    return next;
  }

  function countForOption(products, filters, param, value) {
    // Facet counts should answer: "How many products would match if this value were
    // selected, while keeping every OTHER dimension currently active?"  Therefore
    // ignore only this value's own logical dimension, not every value sharing the
    // same raw URL parameter (especially important for filter.p.tag).
    const filtersForCount = filtersWithoutDimension(filters, param, value);
    const seen = new Set();

    return products.reduce((count, product) => {
      const key = String(product?.id || product?.handle || product?.title || '').trim();
      if (!key || seen.has(key)) return count;
      if (!productMatches(product, filtersForCount)) return count;
      if (!productHasValue(product, param, value)) return count;
      seen.add(key);
      return count + 1;
    }, 0);
  }

  function canCalculateCount(param) {
    return LIVE_COUNT_PARAMS.has(param);
  }

  function fallbackSpecificationRoot() {
    return document.querySelector('#FacetFiltersFormMobile .cosmoshop-specifications-filter--fallback');
  }

  function fallbackSpecificationDrawerIsOpen(root = fallbackSpecificationRoot()) {
    const details = root ? root.closest('.cosmoshop-filter-group--specs, .cosmoshop-filter-group--specifications') : null;
    return Boolean(details && details.open);
  }

  function hasActiveSpecificationFilters() {
    const filters = selectedFilters();
    for (const key of filters.keys()) {
      if (SPEC_PARAMS.has(key)) return true;
    }
    return false;
  }

  async function updateCosmoshopFilterCounts() {
    // Intentionally disabled: per-filter option counts caused unnecessary collection-data fetching
    // and made the filter drawer slower. Product/result filtering still uses the shared data API below.
    return;
  }

  let updateTimer;
  function scheduleCosmoshopFilterCounts() {
    window.clearTimeout(updateTimer);
    updateTimer = window.setTimeout(updateCosmoshopFilterCounts, 60);
  }

  window.cosmoshopFilterData = {
    collectionHandleFromPath,
    fetchCollectionProducts,
    selectedFilters,
    productMatches,
    normalise,
    sameValue,
    valuesForParam,
    countForOption,
    canCalculateCount,
    SPEC_PARAMS,
    LIVE_COUNT_PARAMS
  };

  window.cosmoshopUpdateFilterOptionCounts = function cosmoshopDisabledFilterOptionCounts() {};
})();

/***** Cosmoshop client-side collection filtering fallback *****/
(() => {
  // Core dimensions owned by Shopify Search & Discovery / storefront filtering.
  // Product Type is Shopify Standard: Category (filter.p.t.category or
  // filter.p.category). Promotions are also native, but can arrive through the shared
  // filter.p.tag source, so promotion values are classified separately below instead
  // of marking every tag as native (Gender & Age still use the slower tag fallback).
  const SHOPIFY_NATIVE_FILTER_PARAMS = new Set([
    'filter.v.availability',
    'filter.v.price.gte',
    'filter.v.price.lte',
    'filter.p.vendor',
    'filter.p.t.category',
    // Kept as a compatibility alias for older/custom category URLs. Shopify's
    // taxonomy category filter is normally filter.p.t.category.
    'filter.p.category'
  ]);
  const SUPPORTED_STATIC_PARAMS = new Set([
    ...SHOPIFY_NATIVE_FILTER_PARAMS,
    'filter.p.product_type',
    'filter.p.tag'
  ]);
  const NATIVE_PROMOTION_CONTROL_SELECTOR = [
    '.cosmoshop-filter-group--promotions.cosmoshop-filter-group--shopify-native input[name^="filter."]',
    '.cosmoshop-filter-group--promotions.cosmoshop-filter-group--shopify-native select[name^="filter."]'
  ].join(', ');

  function cleanNativePromotionValue(value) {
    let output = String(value == null ? '' : value);
    try {
      output = decodeURIComponent(output.replace(/\+/g, ' '));
    } catch (error) {
      output = output.replace(/\+/g, ' ');
    }
    return output.replace(/[-_]+/g, ' ').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function canonicalNativePromotionKey(value) {
    const normalized = cleanNativePromotionValue(value);
    if (['pre order', 'preorder', 'new promotion pre order'].includes(normalized)) return 'pre-order';
    if (['final price', 'finalprice', 'new promotion final price'].includes(normalized)) return 'final-price';
    if (['ex demo', 'exdemo', 'new promotion ex demo'].includes(normalized)) return 'ex-demo';
    if (['lq', 'limited quantity', 'limitedquantity', 'limited availability', 'limitedavailability', 'limited-availability', 'new promotion limited quantity', 'new promotion limited availability'].includes(normalized)) return 'limited-quantity';
    return '';
  }

  function nativePromotionControlValues() {
    const valuesByParam = new Map();
    document.querySelectorAll(NATIVE_PROMOTION_CONTROL_SELECTOR).forEach((control) => {
      const param = String(control.getAttribute('name') || '').trim();
      const value = cleanNativePromotionValue(control.value);
      if (!param || !value) return;
      if (!valuesByParam.has(param)) valuesByParam.set(param, new Set());
      valuesByParam.get(param).add(value);
    });
    return valuesByParam;
  }

  function isDedicatedNativePromotionParam(param, promotionControls = nativePromotionControlValues()) {
    return Boolean(param && param !== 'filter.p.tag' && promotionControls.has(param));
  }

  function isNativePromotionFilterValue(param, value, promotionControls = nativePromotionControlValues()) {
    const normalizedValue = cleanNativePromotionValue(value);
    const renderedValues = promotionControls.get(param);
    if (renderedValues && renderedValues.has(normalizedValue)) return true;

    // Compatibility for the standard Search & Discovery Tags source. This also lets
    // a native promotion URL remain fast while the drawer is between AJAX renders.
    return param === 'filter.p.tag' && Boolean(canonicalNativePromotionKey(value));
  }
  const cardPageCache = new Map();
  const filteredResultPageCache = new Map();
  const DEFAULT_AVAILABILITY_PARAM = 'filter.v.availability';
  const DEFAULT_AVAILABILITY_VALUE = '1';
  const SLOW_OPERATION_NOTICE_DELAY = 1000;
  const MAX_PARALLEL_CARD_FETCHES = 4;
  const COLLECTION_STATE_STORAGE_KEY = 'cosmoshop.collectionFilterState.v1';
  const COLLECTION_VIEW_SNAPSHOT_STORAGE_KEY = 'cosmoshop.collectionViewSnapshot.v2';
  const COLLECTION_STATE_TTL = 2 * 60 * 60 * 1000;
  const COLLECTION_VIEW_SNAPSHOT_TTL = 30 * 60 * 1000;
  let renderTimer;
  let pendingRenderOptions = {};
  let renderRunId = 0;

  function collectionPagePath() {
    return /\/collections\/[^/?#]+/.test(window.location.pathname || '');
  }

  function currentCollectionPath() {
    return String(window.location.pathname || '').replace(/\/$/, '');
  }

  function safeStorage() {
    try {
      const storage = window.sessionStorage;
      const testKey = '__cosmoshop_filter_state_test__';
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
      return storage;
    } catch (error) {
      return null;
    }
  }

  function stateSearchParams(search = window.location.search || '') {
    const raw = String(search || '').replace(/^\?/, '');
    const params = new URLSearchParams(raw);
    params.delete('section_id');
    params.delete('sections');
    return params;
  }

  function isDefaultAvailabilityOnly(values) {
    return values.length > 0 && values.every((value) => String(value || '').trim() === DEFAULT_AVAILABILITY_VALUE);
  }

  function hasMeaningfulCollectionState(search = window.location.search || '') {
    const params = stateSearchParams(search);
    let hasMeaningful = false;

    params.forEach((value, key) => {
      const cleaned = String(value || '').trim();
      if (cleaned === '') return;
      if (key === DEFAULT_AVAILABILITY_PARAM) return;
      if (key === 'page') return;
      hasMeaningful = true;
    });

    if (hasMeaningful) return true;

    const availabilityValues = params.getAll(DEFAULT_AVAILABILITY_PARAM);
    return availabilityValues.length > 0 && !isDefaultAvailabilityOnly(availabilityValues);
  }

  function normalisedCollectionSearch(search = window.location.search || '') {
    const params = stateSearchParams(search);
    const query = params.toString();
    return query ? `?${query}` : '';
  }

  function canonicalCollectionSearch(search = window.location.search || '', includePage = true) {
    const params = stateSearchParams(search);
    if (!includePage) params.delete('page');
    const entries = Array.from(params.entries()).sort((a, b) => {
      const keyCompare = a[0].localeCompare(b[0]);
      return keyCompare || a[1].localeCompare(b[1]);
    });
    const canonical = new URLSearchParams();
    entries.forEach(([key, value]) => canonical.append(key, value));
    const query = canonical.toString();
    return query ? `?${query}` : '';
  }

  function collectionViewSignature(search = window.location.search || '') {
    return `${currentCollectionPath()}${canonicalCollectionSearch(search, true)}`;
  }

  function filteredResultBaseKey(pageSize, search = window.location.search || '') {
    return `${currentCollectionPath()}${canonicalCollectionSearch(search, false)}::${Math.max(1, Number(pageSize) || 1)}`;
  }

  function filteredResultPageKey(page, pageSize, search = window.location.search || '') {
    return `${filteredResultBaseKey(pageSize, search)}::page=${Math.max(1, Number(page) || 1)}`;
  }

  function isBackForwardNavigation() {
    try {
      const navigation = window.performance?.getEntriesByType?.('navigation')?.[0];
      if (navigation && navigation.type) return navigation.type === 'back_forward';
      return window.performance?.navigation?.type === 2;
    } catch (error) {
      return false;
    }
  }

  function cameFromProductPage() {
    if (!document.referrer) return false;
    try {
      const referrer = new URL(document.referrer);
      return referrer.origin === window.location.origin && /\/products\/[^/?#]+/.test(referrer.pathname || '');
    } catch (error) {
      return false;
    }
  }

  function readStoredCollectionState() {
    const storage = safeStorage();
    if (!storage) return null;

    try {
      const stored = JSON.parse(storage.getItem(COLLECTION_STATE_STORAGE_KEY) || 'null');
      if (!stored || stored.path !== currentCollectionPath()) return null;
      if (!stored.search || !hasMeaningfulCollectionState(stored.search)) return null;
      if (!stored.expires || stored.expires < Date.now()) {
        storage.removeItem(COLLECTION_STATE_STORAGE_KEY);
        return null;
      }
      return stored;
    } catch (error) {
      storage.removeItem(COLLECTION_STATE_STORAGE_KEY);
      return null;
    }
  }

  function saveCollectionState() {
    if (!collectionPagePath()) return;
    const storage = safeStorage();
    if (!storage) return;

    const search = normalisedCollectionSearch();
    if (!hasMeaningfulCollectionState(search)) return;

    storage.setItem(COLLECTION_STATE_STORAGE_KEY, JSON.stringify({
      path: currentCollectionPath(),
      search,
      expires: Date.now() + COLLECTION_STATE_TTL,
    }));
  }

  function saveCollectionViewSnapshot() {
    if (!collectionPagePath()) return;
    const storage = safeStorage();
    const container = document.getElementById('ProductGridContainer');
    if (!storage || !container) return;

    const productGrid = container.querySelector('#product-grid');
    if (!productGrid) return;
    if (container.classList.contains('loading') || container.getAttribute('aria-busy') === 'true') return;

    const total = Number.parseInt(container.getAttribute('data-total-results') || document.getElementById('ProductCount')?.getAttribute('data-total-results') || '0', 10);
    const pageSize = Number.parseInt(container.getAttribute('data-products-per-page') || document.getElementById('ProductCount')?.getAttribute('data-products-per-page') || String(productGrid.children.length || 12), 10);
    const currentPage = getCurrentPage();
    const dataAttributes = {};
    Array.from(container.attributes).forEach((attribute) => {
      if (attribute.name.startsWith('data-')) dataAttributes[attribute.name] = attribute.value;
    });

    try {
      storage.setItem(COLLECTION_VIEW_SNAPSHOT_STORAGE_KEY, JSON.stringify({
        path: currentCollectionPath(),
        search: canonicalCollectionSearch(),
        signature: collectionViewSignature(),
        containerHtml: container.innerHTML,
        containerClassName: container.className,
        dataAttributes,
        total: Number.isFinite(total) ? total : 0,
        pageSize: Number.isFinite(pageSize) && pageSize > 0 ? pageSize : 12,
        currentPage,
        clientFiltered: document.documentElement.classList.contains('cosmoshop-client-filtered-results'),
        scrollY: Math.max(0, window.scrollY || window.pageYOffset || 0),
        expires: Date.now() + COLLECTION_VIEW_SNAPSHOT_TTL,
      }));
    } catch (error) {
      // A rendered product page can be large. If storage is full, preserve normal
      // navigation rather than allowing a quota exception to interrupt the click.
      try { storage.removeItem(COLLECTION_VIEW_SNAPSHOT_STORAGE_KEY); } catch (ignored) {}
    }
  }

  function readCollectionViewSnapshot() {
    const storage = safeStorage();
    if (!storage) return null;
    try {
      const snapshot = JSON.parse(storage.getItem(COLLECTION_VIEW_SNAPSHOT_STORAGE_KEY) || 'null');
      if (!snapshot || snapshot.path !== currentCollectionPath()) return null;
      if (!snapshot.expires || snapshot.expires < Date.now()) {
        storage.removeItem(COLLECTION_VIEW_SNAPSHOT_STORAGE_KEY);
        return null;
      }
      return snapshot;
    } catch (error) {
      storage.removeItem(COLLECTION_VIEW_SNAPSHOT_STORAGE_KEY);
      return null;
    }
  }

  function finishRestoredCollectionView(container) {
    if (!container) return;
    hideSlowOperationNotice(container);
    container.classList.remove('loading');
    container.removeAttribute('aria-busy');
    if (window.FacetFiltersForm) {
      window.FacetFiltersForm.setLoading(false);
      window.FacetFiltersForm.toggleActiveFacets(false);
      window.FacetFiltersForm.syncUiToSearchParams(window.location.search);
    }
    if (typeof window.cosmoshopRenderActiveFilterChips === 'function') window.cosmoshopRenderActiveFilterChips();
    if (typeof window.cosmoshopApplyProductView === 'function') window.setTimeout(window.cosmoshopApplyProductView, 0);
    if (typeof window.CosmoshopRefreshProductStatusBadgeCarousels === 'function') {
      window.setTimeout(() => window.CosmoshopRefreshProductStatusBadgeCarousels(container), 0);
      window.setTimeout(() => window.CosmoshopRefreshProductStatusBadgeCarousels(container), 160);
    }
    if (typeof initButtons === 'function') window.setTimeout(initButtons, 0);
  }

  function currentRenderedViewMatchesUrl() {
    const container = document.getElementById('ProductGridContainer');
    return Boolean(container && container.dataset.cosmoshopViewSignature === collectionViewSignature());
  }

  function restoreCollectionViewSnapshot(options = {}) {
    if (!collectionPagePath()) return false;
    const container = document.getElementById('ProductGridContainer');
    if (!container) return false;

    // A back-forward cache restoration already contains the exact finished DOM and
    // in-memory card caches. Do not touch or re-filter it.
    if (currentRenderedViewMatchesUrl()) {
      finishRestoredCollectionView(container);
      return true;
    }

    const allowStoredSnapshot = options.force === true || cameFromProductPage() || isBackForwardNavigation();
    if (!allowStoredSnapshot) return false;

    const snapshot = readCollectionViewSnapshot();
    if (!snapshot) return false;
    if (snapshot.search !== canonicalCollectionSearch()) return false;
    if (snapshot.signature !== collectionViewSignature()) return false;
    if (!snapshot.containerHtml) return false;

    container.innerHTML = snapshot.containerHtml;
    if (snapshot.containerClassName) container.className = snapshot.containerClassName;
    Object.entries(snapshot.dataAttributes || {}).forEach(([name, value]) => container.setAttribute(name, value));
    container.dataset.cosmoshopViewSignature = snapshot.signature;
    document.documentElement.classList.toggle('cosmoshop-client-filtered-results', Boolean(snapshot.clientFiltered));

    updateCounts(
      Number(snapshot.total) || 0,
      Number(snapshot.pageSize) || 12,
      Number(snapshot.currentPage) || 1,
      container.querySelectorAll('#product-grid > .grid__item').length,
    );
    finishRestoredCollectionView(container);

    if (!options.keepBrowserScroll && Number.isFinite(Number(snapshot.scrollY))) {
      window.requestAnimationFrame(() => window.scrollTo(0, Number(snapshot.scrollY) || 0));
    }
    return true;
  }

  function clearStoredCollectionStateForCurrentPath() {
    const storage = safeStorage();
    if (!storage) return;

    try {
      const stored = JSON.parse(storage.getItem(COLLECTION_STATE_STORAGE_KEY) || 'null');
      if (stored && stored.path === currentCollectionPath()) storage.removeItem(COLLECTION_STATE_STORAGE_KEY);
    } catch (error) {
      storage.removeItem(COLLECTION_STATE_STORAGE_KEY);
    }
  }

  function restorePersistedCollectionState() {
    if (!collectionPagePath()) return false;

    if (hasMeaningfulCollectionState()) {
      saveCollectionState();
      return false;
    }

    if (!cameFromProductPage()) return false;

    const stored = readStoredCollectionState();
    if (!stored) return false;

    const currentSearch = normalisedCollectionSearch();
    if (currentSearch === stored.search) return false;

    const nextUrl = `${window.location.pathname}${stored.search}${window.location.hash || ''}`;
    const state = { searchParams: stored.search.replace(/^\?/, '') };
    history.replaceState(state, '', nextUrl);

    // When returning from a product, restore the exact rendered collection view in
    // place. Only fall back to a full navigation if no matching snapshot is available.
    if (restoreCollectionViewSnapshot({ force: true })) return true;

    window.location.replace(nextUrl);
    return true;
  }

  function syncPersistedCollectionState() {
    if (!collectionPagePath()) return;

    if (hasMeaningfulCollectionState()) {
      saveCollectionState();
      return;
    }

    if (!cameFromProductPage()) clearStoredCollectionStateForCurrentPath();
  }

  function applyDefaultCollectionAvailabilityFilter() {
    // No automatic availability redirect: it caused an extra full page load before
    // users could interact with filters. Availability stays user-controlled.
    return false;
  }

  function currentAvailabilityKey() {
    const params = new URLSearchParams(window.location.search || '');
    return params.getAll(DEFAULT_AVAILABILITY_PARAM).sort().join('|');
  }

  function appendCurrentAvailabilityFilter(params) {
    const currentParams = new URLSearchParams(window.location.search || '');
    currentParams.getAll(DEFAULT_AVAILABILITY_PARAM).forEach((value) => {
      if (String(value || '').trim() !== '') params.append(DEFAULT_AVAILABILITY_PARAM, value);
    });
    return params;
  }

  function setSlowOperationNoticeProgress(notice, progress) {
    if (!notice) return;
    const percent = Math.max(0, Math.min(100, Math.round(Number(progress) || 0)));
    const circumference = 97.39;
    const ring = notice.querySelector('[data-cosmoshop-filter-progress-ring]');
    const text = notice.querySelector('[data-cosmoshop-filter-progress-text]');

    notice.style.setProperty('--cosmoshop-filter-progress', String(percent));
    if (ring) ring.style.strokeDashoffset = String(circumference * (1 - (percent / 100)));
    if (text) text.textContent = `${percent}%`;
  }

  function showSlowOperationNotice(container) {
    if (!container || container.querySelector('.cosmoshop-client-sort-status')) return;

    const notice = document.createElement('div');
    notice.className = 'cosmoshop-client-sort-status';
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
    notice.innerHTML = '<span class="cosmoshop-client-sort-status__backdrop" aria-hidden="true"></span><span class="cosmoshop-client-sort-status__pill"><span class="cosmoshop-client-sort-status__progress" aria-hidden="true"><svg class="cosmoshop-client-sort-status__progress-svg" viewBox="0 0 36 36" focusable="false"><circle class="cosmoshop-client-sort-status__progress-track" cx="18" cy="18" r="15.5"></circle><circle class="cosmoshop-client-sort-status__progress-ring" data-cosmoshop-filter-progress-ring cx="18" cy="18" r="15.5"></circle></svg><span class="cosmoshop-client-sort-status__progress-text" data-cosmoshop-filter-progress-text>0%</span></span><span>Applying Filters</span></span>';
    container.appendChild(notice);

    const startedAt = window.performance && window.performance.now ? window.performance.now() : Date.now();
    setSlowOperationNoticeProgress(notice, 0);

    notice._cosmoshopProgressTimer = window.setInterval(() => {
      const now = window.performance && window.performance.now ? window.performance.now() : Date.now();
      const elapsed = now - startedAt;
      const staged = elapsed < 1700
        ? Math.min(92, (elapsed / 1700) * 92)
        : Math.min(98, 92 + ((elapsed - 1700) / 2200) * 6);
      setSlowOperationNoticeProgress(notice, staged);
    }, 80);
  }

  function hideSlowOperationNotice(container) {
    if (!container) return;
    container.querySelectorAll('.cosmoshop-client-sort-status').forEach((notice) => {
      if (notice._cosmoshopProgressTimer) window.clearInterval(notice._cosmoshopProgressTimer);
      setSlowOperationNoticeProgress(notice, 100);
      window.setTimeout(() => notice.remove(), 220);
    });
  }

  function getDataApi() {
    return window.cosmoshopFilterData || null;
  }

  function filtersFromSearch(search = window.location.search || '') {
    const params = search instanceof URLSearchParams
      ? new URLSearchParams(search.toString())
      : new URLSearchParams(String(search || '').replace(/^\?/, ''));
    const filters = new Map();
    params.forEach((value, key) => {
      if (!key.startsWith('filter.')) return;
      if (String(value || '').trim() === '') return;
      if (!filters.has(key)) filters.set(key, []);
      filters.get(key).push(value);
    });
    return filters;
  }

  function sortByFromSearch(search = window.location.search || '') {
    try {
      const params = search instanceof URLSearchParams
        ? search
        : new URLSearchParams(String(search || '').replace(/^\?/, ''));
      return params.get('sort_by') || '';
    } catch (error) {
      return '';
    }
  }

  function currentFilters() {
    return filtersFromSearch(window.location.search || '');
  }

  function hasManagedFilters(filters, api) {
    if (!filters || !filters.size || !api) return false;
    const promotionControls = nativePromotionControlValues();
    for (const key of filters.keys()) {
      if (SUPPORTED_STATIC_PARAMS.has(key)) continue;
      if (isDedicatedNativePromotionParam(key, promotionControls)) continue;
      if (api.SPEC_PARAMS && api.SPEC_PARAMS.has && api.SPEC_PARAMS.has(key)) continue;
      return false;
    }
    return true;
  }

  function hasSpecificationFilters(filters, api) {
    if (!filters || !filters.size || !api || !api.SPEC_PARAMS || !api.SPEC_PARAMS.has) return false;
    for (const key of filters.keys()) {
      if (api.SPEC_PARAMS.has(key)) return true;
    }
    return false;
  }

  function hasShopifyNativeFilters(filters) {
    if (!filters || !filters.size) return false;
    const promotionControls = nativePromotionControlValues();
    for (const [key, values] of filters.entries()) {
      if (SHOPIFY_NATIVE_FILTER_PARAMS.has(key)) return true;
      if (isDedicatedNativePromotionParam(key, promotionControls)) return true;
      if (values.some((value) => isNativePromotionFilterValue(key, value, promotionControls))) return true;
    }
    return false;
  }

  function clientEvaluatedFilters(filters, api) {
    const next = new Map();
    if (!filters || !filters.size) return next;
    const promotionControls = nativePromotionControlValues();

    for (const [key, values] of filters.entries()) {
      // Shopify remains authoritative for Availability, Price, Brand, Product Type
      // (Standard: Category) and Promotions. When a slower tag/specification filter is
      // mixed with them, the client fallback evaluates only the slow dimensions and
      // later intersects the result with Shopify's native server-rendered product set.
      if (SHOPIFY_NATIVE_FILTER_PARAMS.has(key)) continue;
      if (isDedicatedNativePromotionParam(key, promotionControls)) continue;
      if (key === 'filter.p.tag') {
        const slowTagValues = values.filter(
          (value) => !isNativePromotionFilterValue(key, value, promotionControls)
        );
        if (slowTagValues.length) next.set(key, slowTagValues);
        continue;
      }
      if (key === 'filter.p.product_type') {
        next.set(key, values);
        continue;
      }
      if (api?.SPEC_PARAMS && api.SPEC_PARAMS.has && api.SPEC_PARAMS.has(key)) {
        next.set(key, values);
      }
    }
    return next;
  }

  function shouldUseClientCollectionGrid(filters, sortBy, api) {
    // All filters displayed by this theme are supplied by collection.filters or
    // search.filters (Shopify Search & Discovery). Shopify handles intersection,
    // counts, and pagination on the server. Never launch products.json collection
    // scans for a native filter selection, even when its param is a metafield or tag.
    // The ONLY client-side exception is the separately offered custom sort.
    return Boolean(api && isLargestSavingsSort(sortBy));
  }

  function getSortBy() {
    try {
      return new URLSearchParams(window.location.search || '').get('sort_by') || '';
    } catch (error) {
      return '';
    }
  }

  function isLargestSavingsSort(sortBy) {
    return sortBy === 'largest-savings';
  }


  function getCardDiscountPercent(card) {
    if (!card) return 0;
    const badge = card.querySelector('[data-cosmoshop-sale-percent], .grid-product__on-sale .badge, .card__badge .badge');
    if (!badge) return 0;

    const fromData = Number.parseFloat(badge.getAttribute('data-cosmoshop-sale-percent') || '');
    if (Number.isFinite(fromData)) return fromData;

    const match = String(badge.textContent || '').match(/(\d+(?:\.\d+)?)\s*%\s*off/i);
    return match ? Number.parseFloat(match[1]) || 0 : 0;
  }

  function sortVisibleGridByLargestSavingsBadge() {
    if (!isLargestSavingsSort(getSortBy())) return false;
    const grid = document.querySelector('#ProductGridContainer #product-grid');
    if (!grid) return false;

    const cards = Array.from(grid.children).filter((child) => child.classList && child.classList.contains('grid__item'));
    if (cards.length < 2) return false;

    const sorted = cards
      .map((card, index) => ({ card, index, percent: getCardDiscountPercent(card) }))
      .sort((a, b) => {
        const percentDiff = b.percent - a.percent;
        if (percentDiff !== 0) return percentDiff;
        return a.index - b.index;
      });

    const fragment = document.createDocumentFragment();
    sorted.forEach((entry) => fragment.appendChild(entry.card));
    grid.appendChild(fragment);
    return true;
  }

  window.cosmoshopSortVisibleGridByLargestSavings = sortVisibleGridByLargestSavingsBadge;

  function nativeSortForFetch(sortBy) {
    return isLargestSavingsSort(sortBy) ? '' : sortBy;
  }

  function sortProductsForLargestSavings(products) {
    return [...products].sort((a, b) => {
      const savingDiff = (Number(b.largestSaving) || 0) - (Number(a.largestSaving) || 0);
      if (savingDiff !== 0) return savingDiff;
      const priceDiff = (Number(a.minPrice) || 0) - (Number(b.minPrice) || 0);
      if (priceDiff !== 0) return priceDiff;
      return String(a.title || a.handle || '').localeCompare(String(b.title || b.handle || ''));
    });
  }

  function orderCardsByProducts(cards, products) {
    const byHandle = new Map(cards.map((card) => [card.handle, card]));
    return products.map((product) => byHandle.get(product.handle)).filter(Boolean);
  }

  function getCurrentPage() {
    try {
      const page = Number.parseInt(new URLSearchParams(window.location.search || '').get('page') || '1', 10);
      return Number.isFinite(page) && page > 0 ? page : 1;
    } catch (error) {
      return 1;
    }
  }

  function getPageSize() {
    const container = document.getElementById('ProductGridContainer');
    const count = document.getElementById('ProductCount');
    const fromContainer = Number.parseInt(container?.getAttribute('data-products-per-page') || '', 10);
    const fromCount = Number.parseInt(count?.getAttribute('data-products-per-page') || '', 10);
    if (Number.isFinite(fromContainer) && fromContainer > 0) return fromContainer;
    if (Number.isFinite(fromCount) && fromCount > 0) return fromCount;
    const visible = document.querySelectorAll('#ProductGridContainer #product-grid > .grid__item').length;
    return visible || 12;
  }

  function getSectionId() {
    return document.querySelector('#ProductGridContainer #product-grid')?.dataset?.id
      || document.getElementById('ProductGridContainer')?.dataset?.sectionId
      || document.querySelector('#main-collection-product-grid[data-id]')?.dataset?.id
      || document.querySelector('#main-collection-filters[data-id]')?.dataset?.id
      || document.querySelector('[data-cosmoshop-filter-toolbar]')?.closest('[data-id]')?.dataset?.id
      || '';
  }

  function handleFromCard(card) {
    const fromData = card.querySelector('[data-product-handle]')?.getAttribute('data-product-handle');
    if (fromData) return fromData;
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return '';
    try {
      const url = new URL(link.getAttribute('href'), window.location.origin);
      const match = url.pathname.match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    } catch (error) {
      const match = String(link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      return match ? decodeURIComponent(match[1]) : '';
    }
  }

  function normalisePaginationParams(params) {
    params.delete('section_id');
    params.delete('sections');
    return params;
  }

  function urlForPage(page) {
    const params = normalisePaginationParams(new URLSearchParams(window.location.search || ''));
    params.set('page', String(page));
    const query = params.toString();
    return `${window.location.pathname}${query ? `?${query}` : ''}`;
  }

  function updateUrlForPage(page, replace = false) {
    const nextUrl = urlForPage(page);
    const currentUrl = `${window.location.pathname}${window.location.search || ''}`;
    if (nextUrl === currentUrl) return;
    const state = { searchParams: nextUrl.includes('?') ? nextUrl.slice(nextUrl.indexOf('?') + 1) : '' };
    if (replace) history.replaceState(state, '', nextUrl);
    else history.pushState(state, '', nextUrl);
  }

  function shopifyNativeParamsFromSearch(search = window.location.search || '') {
    const source = search instanceof URLSearchParams
      ? search
      : new URLSearchParams(String(search || '').replace(/^\?/, ''));
    const params = new URLSearchParams();
    const promotionControls = nativePromotionControlValues();
    source.forEach((value, key) => {
      if (!String(value || '').trim()) return;
      if (SHOPIFY_NATIVE_FILTER_PARAMS.has(key)) {
        params.append(key, value);
        return;
      }
      if (isDedicatedNativePromotionParam(key, promotionControls)) {
        params.append(key, value);
        return;
      }
      if (isNativePromotionFilterValue(key, value, promotionControls)) {
        params.append(key, value);
      }
    });
    return params;
  }

  function sectionFetchUrl(page, sectionId, sortBy) {
    // If a slow Gender/Age tag or specification filter is combined with a native
    // Search & Discovery filter (including Promotions), fetch cards from Shopify's
    // already-native-filtered universe. This keeps mixed states correct without making
    // native-only filtering pay the client-side collection-index cost.
    const params = shopifyNativeParamsFromSearch();
    if (sectionId) params.set('section_id', sectionId);
    if (sortBy) params.set('sort_by', sortBy);
    if (page > 1) params.set('page', String(page));
    else params.delete('page');
    return `${window.location.pathname}?${params.toString()}`;
  }

  async function fetchPageCards(page, sectionId, sortBy) {
    const url = sectionFetchUrl(page, sectionId, sortBy);
    if (cardPageCache.has(url)) return cardPageCache.get(url);

    const promise = fetch(url, { credentials: 'same-origin' })
      .then((response) => response.ok ? response.text() : '')
      .then((html) => {
        if (!html) return [];
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return Array.from(doc.querySelectorAll('#ProductGridContainer #product-grid > .grid__item'))
          .map((card) => ({ handle: handleFromCard(card), html: card.outerHTML }))
          .filter((card) => card.handle && card.html);
      })
      .catch((error) => {
        cardPageCache.delete(url);
        throw error;
      });

    cardPageCache.set(url, promise);
    return promise;
  }

  async function fetchCardPages(pageNumbers, sectionId, sortBy, pageSize, cardByHandle) {
    const sortedPages = Array.from(new Set(pageNumbers)).filter((page) => page > 0).sort((a, b) => a - b);

    for (let start = 0; start < sortedPages.length; start += MAX_PARALLEL_CARD_FETCHES) {
      const batch = sortedPages.slice(start, start + MAX_PARALLEL_CARD_FETCHES);
      const results = await Promise.all(batch.map(async (page) => ({
        page,
        cards: await fetchPageCards(page, sectionId, sortBy).catch(() => []),
      })));

      results.forEach(({ cards }) => {
        cards.forEach((card) => {
          if (!cardByHandle.has(card.handle)) cardByHandle.set(card.handle, card);
        });
      });
    }
  }

  async function getFilteredCardPagesThrough(matchedHandles, currentPage, throughPage, pageSize, sectionId, sortBy, totalCollectionProducts) {
    const pages = new Map();
    if (!matchedHandles.size) return pages;

    const safeCurrentPage = Math.max(1, Number(currentPage) || 1);
    const safeThroughPage = Math.max(safeCurrentPage, Number(throughPage) || safeCurrentPage);
    const neededEnd = safeThroughPage * pageSize;
    const totalPages = Math.max(1, Math.ceil(totalCollectionProducts / Math.max(pageSize, 1)));
    const matchedCards = [];

    // Read the server-rendered collection pages in Shopify's actual sort order. The
    // first filtering pass deliberately continues through the following filtered page,
    // so Page 2 is already assembled when Page 1 becomes interactive.
    for (let startPage = 1; startPage <= totalPages && matchedCards.length < neededEnd; startPage += MAX_PARALLEL_CARD_FETCHES) {
      const pageNumbers = [];
      for (let page = startPage; page < startPage + MAX_PARALLEL_CARD_FETCHES && page <= totalPages; page += 1) {
        pageNumbers.push(page);
      }

      const results = await Promise.all(pageNumbers.map(async (page) => ({
        page,
        cards: await fetchPageCards(page, sectionId, sortBy).catch(() => []),
      })));

      results
        .sort((a, b) => a.page - b.page)
        .forEach(({ cards }) => {
          cards.forEach((card) => {
            if (matchedHandles.has(card.handle)) matchedCards.push(card);
          });
        });
    }

    for (let page = safeCurrentPage; page <= safeThroughPage; page += 1) {
      const start = (page - 1) * pageSize;
      pages.set(page, matchedCards.slice(start, start + pageSize));
    }
    return pages;
  }

  async function getAllNativeFilteredMatchingCards(matchedHandles, pageSize, sectionId, sortBy, totalCollectionProducts) {
    if (!matchedHandles.size) return [];

    const safePageSize = Math.max(1, Number(pageSize) || 1);
    const maxPages = Math.max(1, Math.ceil(Math.max(0, Number(totalCollectionProducts) || 0) / safePageSize));
    const matchedCards = [];
    const seenHandles = new Set();

    // The request URL contains only native Search & Discovery filters (Availability,
    // Price, Product Type/Category, Brand and Promotions), so Shopify resolves them before
    // this slower fallback sees a card. Scan only as far as the native result exists, and intersect by handle.
    for (let startPage = 1; startPage <= maxPages; startPage += MAX_PARALLEL_CARD_FETCHES) {
      const pageNumbers = [];
      for (let page = startPage; page < startPage + MAX_PARALLEL_CARD_FETCHES && page <= maxPages; page += 1) {
        pageNumbers.push(page);
      }

      const results = await Promise.all(pageNumbers.map(async (page) => ({
        page,
        cards: await fetchPageCards(page, sectionId, sortBy).catch(() => []),
      })));

      let reachedEnd = false;
      results
        .sort((a, b) => a.page - b.page)
        .forEach(({ cards }) => {
          if (cards.length < safePageSize) reachedEnd = true;
          cards.forEach((card) => {
            if (!matchedHandles.has(card.handle) || seenHandles.has(card.handle)) return;
            seenHandles.add(card.handle);
            matchedCards.push(card);
          });
        });

      if (reachedEnd || seenHandles.size >= matchedHandles.size) break;
    }

    return matchedCards;
  }

  async function getCardsForProducts(handle, allProducts, targetProducts, pageSize, sectionId, sortBy) {
    if (!targetProducts.length) return [];

    const targetHandles = targetProducts.map((product) => product.handle).filter(Boolean);
    const targetSet = new Set(targetHandles);
    const productIndex = new Map(allProducts.map((product, index) => [product.handle, Number.isFinite(product.sourceIndex) ? product.sourceIndex : index]));
    const predictedPages = targetHandles.map((productHandle) => {
      const index = productIndex.get(productHandle);
      return Number.isFinite(index) ? Math.floor(index / Math.max(pageSize, 1)) + 1 : 0;
    }).filter(Boolean);

    const cardByHandle = new Map();
    await fetchCardPages(predictedPages, sectionId, sortBy, pageSize, cardByHandle);

    // products.json normally follows the requested collection sort, so the predicted
    // pages above contain the required cards. If Shopify returns a different order for
    // a particular collection, scan only the remaining pages until every target card
    // has been found instead of downloading the entire collection up front.
    const missing = () => targetHandles.some((productHandle) => !cardByHandle.has(productHandle));
    if (missing()) {
      const totalPages = Math.max(1, Math.ceil(allProducts.length / Math.max(pageSize, 1)));
      const predictedSet = new Set(predictedPages);
      for (let startPage = 1; startPage <= totalPages && missing(); startPage += MAX_PARALLEL_CARD_FETCHES) {
        const fallbackPages = [];
        for (let page = startPage; page < startPage + MAX_PARALLEL_CARD_FETCHES && page <= totalPages; page += 1) {
          if (!predictedSet.has(page)) fallbackPages.push(page);
        }
        if (fallbackPages.length) await fetchCardPages(fallbackPages, sectionId, sortBy, pageSize, cardByHandle);
      }
    }

    return targetHandles.map((productHandle) => cardByHandle.get(productHandle)).filter((card) => card && targetSet.has(card.handle));
  }


  function cacheFilteredResultPage(page, pageSize, cards, total, search = window.location.search || '') {
    filteredResultPageCache.set(filteredResultPageKey(page, pageSize, search), {
      page: Math.max(1, Number(page) || 1),
      pageSize: Math.max(1, Number(pageSize) || 1),
      total: Math.max(0, Number(total) || 0),
      cards: Array.isArray(cards) ? cards.map((card) => ({ handle: card.handle || '', html: card.html || '' })).filter((card) => card.html) : [],
      signature: collectionViewSignature(search),
      createdAt: Date.now(),
    });
  }

  function getCachedFilteredResultPage(page = getCurrentPage(), pageSize = getPageSize(), search = window.location.search || '') {
    return filteredResultPageCache.get(filteredResultPageKey(page, pageSize, search)) || null;
  }

  function markRenderedCollectionView(container) {
    if (!container) return;
    container.dataset.cosmoshopViewSignature = collectionViewSignature();
  }

  function renderCachedFilteredPage(options = {}) {
    const api = getDataApi();
    const filters = currentFilters();
    const sortBy = getSortBy();
    if (!shouldUseClientCollectionGrid(filters, sortBy, api)) return false;

    const pageSize = getPageSize();
    const currentPage = getCurrentPage();
    const cached = getCachedFilteredResultPage(currentPage, pageSize);
    if (!cached) return false;

    ++renderRunId;
    window.clearTimeout(renderTimer);
    pendingRenderOptions = {};
    const container = document.getElementById('ProductGridContainer');
    if (!container) return false;

    hideSlowOperationNotice(container);
    setProductGrid(container, cached.cards.map((card) => card.html));
    renderPagination(cached.total, cached.pageSize, cached.page);
    updateCounts(cached.total, cached.pageSize, cached.page, cached.cards.length);
    markRenderedCollectionView(container);
    document.documentElement.classList.add('cosmoshop-client-filtered-results');
    finishRestoredCollectionView(container);

    if (options.prefetch !== false) {
      window.setTimeout(() => {
        if (cached.page > 1) prefetchFilteredResultPage(cached.page - 1, cached.pageSize);
        prefetchFilteredResultPage(cached.page + 1, cached.pageSize);
      }, 0);
    }
    return true;
  }

  async function prefetchFilteredResultPage(page, pageSize = getPageSize()) {
    const targetPage = Math.max(1, Number(page) || 1);
    if (getCachedFilteredResultPage(targetPage, pageSize)) return;

    const api = getDataApi();
    const handle = api?.collectionHandleFromPath ? api.collectionHandleFromPath() : '';
    const filters = currentFilters();
    const sortBy = getSortBy();
    if (!handle || !shouldUseClientCollectionGrid(filters, sortBy, api)) return;

    const searchAtStart = window.location.search || '';
    const sectionId = getSectionId();
    try {
      const products = await api.fetchCollectionProducts(handle);
      const clientFilters = clientEvaluatedFilters(filters, api);
      let matchedProducts = clientFilters.size
        ? products.filter((product) => api.productMatches(product, clientFilters))
        : [...products];
      const nativeSort = nativeSortForFetch(sortBy);
      const nativeFiltersActive = hasShopifyNativeFilters(filters);
      let nativeMatchedCards = null;

      if (nativeFiltersActive) {
        const clientMatchedHandles = new Set(matchedProducts.map((product) => product.handle));
        nativeMatchedCards = await getAllNativeFilteredMatchingCards(
          clientMatchedHandles,
          pageSize,
          sectionId,
          nativeSort,
          products.length,
        );
        const nativeMatchedHandles = new Set(nativeMatchedCards.map((card) => card.handle));
        matchedProducts = matchedProducts.filter((product) => nativeMatchedHandles.has(product.handle));
      }

      const total = nativeMatchedCards ? nativeMatchedCards.length : matchedProducts.length;
      const totalPages = Math.max(1, Math.ceil(total / Math.max(pageSize, 1)));
      if (targetPage > totalPages) return;

      let cards = [];
      if (isLargestSavingsSort(sortBy)) {
        const orderedProducts = sortProductsForLargestSavings(matchedProducts);
        const start = (targetPage - 1) * pageSize;
        const productsForPage = orderedProducts.slice(start, start + pageSize);
        if (nativeMatchedCards) {
          const cardByHandle = new Map(nativeMatchedCards.map((card) => [card.handle, card]));
          cards = productsForPage.map((product) => cardByHandle.get(product.handle)).filter(Boolean);
        } else {
          cards = await getCardsForProducts(handle, products, productsForPage, pageSize, sectionId, nativeSort);
        }
      } else if (nativeMatchedCards) {
        const start = (targetPage - 1) * pageSize;
        cards = nativeMatchedCards.slice(start, start + pageSize);
      } else {
        const matchedHandles = new Set(matchedProducts.map((product) => product.handle));
        const pageMap = await getFilteredCardPagesThrough(matchedHandles, targetPage, targetPage, pageSize, sectionId, nativeSort, products.length);
        cards = pageMap.get(targetPage) || [];
      }
      cacheFilteredResultPage(targetPage, pageSize, cards, total, searchAtStart);
    } catch (error) {
      // Prefetching is an optimisation only; normal pagination remains available.
    }
  }


  function renderEmptyState(container) {
    const sectionId = getSectionId();
    if (sectionId) container.dataset.sectionId = sectionId;
    container.innerHTML = `
      <div class="collection collection--empty page-width" id="main-collection-product-grid"${sectionId ? ` data-id="${sectionId}"` : ''}>
        <div class="title-wrapper center">
          <h2 class="title title--primary">No products found</h2>
        </div>
      </div>`;
  }

  function ensureProductGrid(container) {
    let grid = container.querySelector('#product-grid');
    if (grid) return grid;

    const sectionId = getSectionId();
    if (sectionId) container.dataset.sectionId = sectionId;
    container.innerHTML = `
      <div class="collection page-width">
        <div id="products" class="row list-group">
          <ul id="product-grid"${sectionId ? ` data-id="${sectionId}"` : ''} class="item grid grid--2-col grid--3-col-tablet grid--4-col-desktop grid--one-third-max grid--quarter-max negative-margin"></ul>
        </div>
      </div>`;
    return container.querySelector('#product-grid');
  }

  function setProductGrid(container, cardHtmlList) {
    if (!cardHtmlList.length) {
      renderEmptyState(container);
      return null;
    }

    const grid = ensureProductGrid(container);
    grid.innerHTML = cardHtmlList.join('');
    return grid;
  }

  function paginationArrowIcon() {
    // Keep the dynamically generated pagination visually identical to the theme's
    // original snippets/icon-lag.liquid output. The pagination CSS rotates this same
    // downward chevron for the previous and next controls.
    return '<svg xmlns="http://www.w3.org/2000/svg" class="icon icon-lag" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M24 24H0V0h24v24z" fill="none" opacity=".87"></path><path d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6-1.41-1.41z"></path></svg>';
  }

  function renderPagination(total, pageSize, currentPage) {
    document.querySelectorAll('#ProductGridContainer .pagination-wrapper, #ProductGridContainer [data-cosmoshop-client-pagination]').forEach((node) => node.remove());
    if (total <= pageSize) return;

    const pages = Math.ceil(total / pageSize);
    const safeCurrent = Math.min(Math.max(currentPage, 1), pages);
    const wrapper = document.createElement('div');
    // Use the exact original wrapper class so component-pagination.css restores the
    // collection pagination's normal spacing, sizing, hover effects and alignment.
    wrapper.className = 'pagination-wrapper';
    wrapper.setAttribute('data-cosmoshop-client-pagination', '');

    const items = [];
    const addPage = (page) => {
      if (page === safeCurrent) {
        items.push(`<li><a href="${urlForPage(page)}" class="pagination__item pagination__item--current link" data-cosmoshop-client-page="${page}" aria-current="page" aria-label="Page ${page}">${page}</a></li>`);
      } else {
        items.push(`<li><a href="${urlForPage(page)}" class="pagination__item link" data-cosmoshop-client-page="${page}" aria-label="Page ${page}">${page}</a></li>`);
      }
    };

    // Cosmoshop: expose every available page in one horizontally scrollable strip.
    // This intentionally replaces Shopify's compact first/last/ellipsis pagination.
    for (let page = 1; page <= pages; page += 1) addPage(page);

    wrapper.setAttribute('data-cosmoshop-pagination-pages', String(pages));
    wrapper.setAttribute('data-cosmoshop-pagination-current', String(safeCurrent));
    wrapper.innerHTML = `<nav class="pagination" role="navigation" aria-label="Pagination"><ul class="pagination__list list-unstyled" role="list">${items.join('')}</ul></nav>`;

    // The original Liquid pagination sits directly after #product-grid inside
    // #products. Append it in that same position instead of outside the product row.
    const productsWrapper = document.querySelector('#ProductGridContainer #products') || document.querySelector('#ProductGridContainer .collection.page-width') || document.getElementById('ProductGridContainer');
    productsWrapper.appendChild(wrapper);
    if (typeof window.cosmoshopEnhanceProductPagination === 'function') {
      window.requestAnimationFrame(() => window.cosmoshopEnhanceProductPagination(wrapper));
    }
  }

  function updateCounts(total, pageSize, currentPage, visibleCount) {
    const shown = total ? Math.min(((currentPage - 1) * pageSize) + visibleCount, total) : 0;
    const text = `${shown} of ${total}`;
    [document.getElementById('ProductCount'), document.getElementById('ProductCountDesktop')].forEach((target) => {
      if (!target) return;
      target.textContent = text;
      target.setAttribute('data-total-results', String(total));
      target.setAttribute('data-products-per-page', String(pageSize));
      target.setAttribute('data-current-page', String(currentPage));
      target.setAttribute('data-count-text', text);
      target.classList.remove('loading');
    });

    document.querySelectorAll('.mobile-facets__count').forEach((node) => {
      node.textContent = `${total} product${total === 1 ? '' : 's'}`;
    });

    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) {
      gridContainer.setAttribute('data-total-results', String(total));
      gridContainer.setAttribute('data-products-per-page', String(pageSize));
    }

    if (window.cosmoshopUpdateProductCountProgress) {
      window.setTimeout(window.cosmoshopUpdateProductCountProgress, 0);
    }
  }

  async function renderClientFilteredGrid(options = {}) {
    // A cached pagination state or a restored product-return snapshot is already the
    // finished result. Never show the filter overlay or run the index again for it.
    if (options.useCache !== false && renderCachedFilteredPage({ prefetch: true })) return;

    // Increment before any early return so an in-flight older fallback can never
    // overwrite a newer native-filter state after a checkbox is unticked.
    const thisRun = ++renderRunId;

    if (restorePersistedCollectionState()) return;
    syncPersistedCollectionState();
    if (applyDefaultCollectionAvailabilityFilter()) return;

    const api = getDataApi();
    const handle = api?.collectionHandleFromPath ? api.collectionHandleFromPath() : '';
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    const filters = currentFilters();
    const sortBy = getSortBy();
    const largestSavingsSort = isLargestSavingsSort(sortBy);
    const suppressNotice = Boolean(options.suppressNotice);

    if (!handle) return;
    if (!shouldUseClientCollectionGrid(filters, sortBy, api)) {
      document.documentElement.classList.remove('cosmoshop-client-filtered-results');
      hideSlowOperationNotice(container);
      container.classList.remove('loading');
      container.removeAttribute('aria-busy');
      container.removeAttribute('data-cosmoshop-view-signature');
      if (window.FacetFiltersForm) window.FacetFiltersForm.toggleActiveFacets(false);
      return;
    }
    const pageSize = getPageSize();
    const sectionId = getSectionId();
    const slowNoticeTimer = suppressNotice ? null : window.setTimeout(() => {
      if (thisRun === renderRunId) showSlowOperationNotice(container);
    }, SLOW_OPERATION_NOTICE_DELAY);

    if (!suppressNotice) container.classList.add('loading');
    container.setAttribute('aria-busy', 'true');

    try {
      const nativeSort = nativeSortForFetch(sortBy);
      const products = await api.fetchCollectionProducts(handle);
      if (thisRun !== renderRunId) return;

      const clientFilters = clientEvaluatedFilters(filters, api);
      let matchedProducts = clientFilters.size
        ? products.filter((product) => api.productMatches(product, clientFilters))
        : [...products];
      const nativeFiltersActive = hasShopifyNativeFilters(filters);
      let nativeMatchedCards = null;

      if (nativeFiltersActive) {
        const clientMatchedHandles = new Set(matchedProducts.map((product) => product.handle));
        nativeMatchedCards = await getAllNativeFilteredMatchingCards(
          clientMatchedHandles,
          pageSize,
          sectionId,
          nativeSort,
          products.length,
        );
        if (thisRun !== renderRunId) return;
        const nativeMatchedHandles = new Set(nativeMatchedCards.map((card) => card.handle));
        matchedProducts = matchedProducts.filter((product) => nativeMatchedHandles.has(product.handle));
      }

      const total = nativeMatchedCards ? nativeMatchedCards.length : matchedProducts.length;
      const totalPages = Math.max(1, Math.ceil(total / Math.max(pageSize, 1)));
      let currentPage = Math.min(getCurrentPage(), totalPages);
      if (currentPage < 1) currentPage = 1;
      if (getCurrentPage() !== currentPage) updateUrlForPage(currentPage, true);
      const throughPage = Math.min(totalPages, currentPage + 1);
      const pagesToCache = new Map();

      if (largestSavingsSort) {
        const orderedProducts = sortProductsForLargestSavings(matchedProducts);
        const start = (currentPage - 1) * pageSize;
        const productsThroughNextPage = orderedProducts.slice(start, throughPage * pageSize);
        let cards = [];
        if (nativeMatchedCards) {
          const cardByHandle = new Map(nativeMatchedCards.map((card) => [card.handle, card]));
          cards = productsThroughNextPage.map((product) => cardByHandle.get(product.handle)).filter(Boolean);
        } else {
          cards = await getCardsForProducts(handle, products, productsThroughNextPage, pageSize, sectionId, nativeSort);
        }
        for (let page = currentPage; page <= throughPage; page += 1) {
          const offset = (page - currentPage) * pageSize;
          pagesToCache.set(page, cards.slice(offset, offset + pageSize));
        }
      } else if (nativeMatchedCards) {
        for (let page = currentPage; page <= throughPage; page += 1) {
          const start = (page - 1) * pageSize;
          pagesToCache.set(page, nativeMatchedCards.slice(start, start + pageSize));
        }
      } else {
        const matchedHandles = new Set(matchedProducts.map((product) => product.handle));
        const fetchedPages = await getFilteredCardPagesThrough(matchedHandles, currentPage, throughPage, pageSize, sectionId, nativeSort, products.length);
        fetchedPages.forEach((cards, page) => pagesToCache.set(page, cards));
      }
      if (thisRun !== renderRunId) return;

      pagesToCache.forEach((cards, page) => cacheFilteredResultPage(page, pageSize, cards, total));
      const cardsForPage = pagesToCache.get(currentPage) || [];
      const htmlForPage = cardsForPage.map((card) => card.html);
      setProductGrid(container, htmlForPage);
      if (typeof window.cosmoshopApplyProductView === 'function') window.setTimeout(window.cosmoshopApplyProductView, 0);
      renderPagination(total, pageSize, currentPage);
      updateCounts(total, pageSize, currentPage, htmlForPage.length);
      markRenderedCollectionView(container);

      // Cache/prefetch both directions so repeated page navigation under the same
      // filter state is normally a zero-network DOM swap.
      window.setTimeout(() => {
        if (currentPage > 1) prefetchFilteredResultPage(currentPage - 1, pageSize);
        prefetchFilteredResultPage(currentPage + 1, pageSize);
      }, 0);

      document.documentElement.classList.toggle('cosmoshop-client-filtered-results', filters.size > 0);
      if (window.cosmoshopRenderActiveFilterChips) window.setTimeout(window.cosmoshopRenderActiveFilterChips, 0);
      if (typeof initButtons === 'function') window.setTimeout(initButtons, 0);
    } catch (error) {
      // If anything unexpected happens, leave Shopify's native filtered result in place.
    } finally {
      if (slowNoticeTimer) window.clearTimeout(slowNoticeTimer);
      if (thisRun === renderRunId) {
        hideSlowOperationNotice(container);
        if (window.FacetFiltersForm) window.FacetFiltersForm.toggleActiveFacets(false);
      }
      container.classList.remove('loading');
      container.removeAttribute('aria-busy');
    }
  }

  function scheduleClientFilteredGrid(options = {}) {
    pendingRenderOptions = { ...pendingRenderOptions, ...options };
    window.clearTimeout(renderTimer);
    renderTimer = window.setTimeout(() => {
      const nextOptions = pendingRenderOptions;
      pendingRenderOptions = {};
      renderClientFilteredGrid(nextOptions);
    }, 80);
  }

  window.cosmoshopShouldUseClientGridForSearch = function cosmoshopShouldUseClientGridForSearch(searchParams = window.location.search || '') {
    if (!collectionPagePath()) return false;
    const api = getDataApi();
    if (!api) return false;
    return shouldUseClientCollectionGrid(filtersFromSearch(searchParams), sortByFromSearch(searchParams), api);
  };
  window.cosmoshopRenderClientFilteredGridNow = renderClientFilteredGrid;
  window.cosmoshopRenderClientFilteredGrid = scheduleClientFilteredGrid;
  window.cosmoshopRestoreCachedClientFilteredPage = function cosmoshopRestoreCachedClientFilteredPage() {
    return renderCachedFilteredPage({ prefetch: true });
  };

  document.addEventListener('DOMContentLoaded', () => {
    if (!restoreCollectionViewSnapshot()) scheduleClientFilteredGrid();
  });
  window.addEventListener('pageshow', (event) => {
    if (restoreCollectionViewSnapshot({ keepBrowserScroll: Boolean(event.persisted) })) return;
    scheduleClientFilteredGrid();
  });
  // FacetFiltersForm handles popstate by restoring a cached page first, or by
  // rendering Shopify's section before a single client fallback when no cache exists.
  document.addEventListener('shopify:section:load', () => scheduleClientFilteredGrid());

  document.addEventListener('click', (event) => {
    const productLink = event.target.closest('a[href*="/products/"]');
    if (productLink && collectionPagePath()) {
      saveCollectionState();
      saveCollectionViewSnapshot();
    }

  }, true);


  // Filter renders remain driven by the single FacetFiltersForm state machine.
  // Pagination now uses the same history state without reloading the storefront shell.
})();


/***** Cosmoshop URL active filter chips *****/
(() => {
  const CHIP_SELECTOR = '[data-cosmoshop-url-active-chip]';
  const CLEAR_WRAPPER_SELECTOR = '.active-facets__button-wrapper';
  const SEARCH_SECTION_KEYS = new Set(['page', 'sections', 'section_id']);

  function cleanText(value) {
    try {
      value = decodeURIComponent(String(value || '').replace(/\+/g, ' '));
    } catch (error) {
      value = String(value || '').replace(/\+/g, ' ');
    }
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function moneyText(value) {
    const number = Number(cleanText(value));
    if (!Number.isFinite(number)) return cleanText(value);
    return number.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  function stripCount(text) {
    return cleanText(text).replace(/\s*\(\d+\)\s*$/, '').trim();
  }

  function currentUrlParams() {
    return new URLSearchParams(window.location.search || '');
  }

  function labelFromForm(param, value) {
    const inputs = document.querySelectorAll('#FacetFiltersFormMobile input[name]');
    for (const input of inputs) {
      if (input.name !== param) continue;
      if (String(input.value) !== String(value)) continue;
      const label = input.closest('label');
      if (!label) continue;
      const labelNode = label.querySelector('[data-cosmoshop-filter-label]');
      if (labelNode && stripCount(labelNode.textContent)) return stripCount(labelNode.textContent);
      const clone = label.cloneNode(true);
      clone.querySelectorAll('input, svg, [data-cosmoshop-filter-count], .mobile-facets__highlight').forEach((node) => node.remove());
      const text = stripCount(clone.textContent);
      if (text) return text;
    }
    return '';
  }

  function normalizeUnitLabel(param, label) {
    let output = cleanText(label);
    if (param === 'filter.p.m.custom.case_diameter_mm' || param === 'filter.p.m.custom.case_thickness_mm') {
      output = output.replace(/\bmm\b/gi, 'mm');
    }
    if (param === 'filter.p.m.custom.water_resistance_m') {
      output = output.replace(/(\d)\s*m\b/gi, '$1 m');
    }
    return output;
  }


  function titleCaseWords(value) {
    return cleanText(value)
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => {
        const lower = part.toLowerCase();
        if (['mop', 'uv', 'atm', 'gps', 'gmt', 'tac'].includes(lower)) return lower.toUpperCase();
        if (['mm', 'cm', 'm', 'h'].includes(lower)) return lower;
        if (lower === 'v') return 'V';
        if (lower === 'mah') return 'mAh';
        if (lower === 'bluetooth') return 'Bluetooth';
        if (lower === 'cr-39') return 'CR-39';
        if (/^(?:cr|sr)\d+[a-z]*$/i.test(part)) return part.toUpperCase();
        if (lower === 'tr90') return 'TR90';
        if (lower === 'uv400') return 'UV400';
        if (lower === 'eco-drive') return 'Eco-Drive';
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      })
      .join(' ');
  }

  function formatCurrentNewTagValue(rawValue) {
    const raw = cleanText(rawValue);
    const lower = raw.toLowerCase();
    if (!raw) return '';
    if (lower === 'men') return 'Men';
    if (lower === 'women') return 'Women';
    if (lower === 'unisex') return 'Unisex';
    if (lower === 'adults') return 'Adults';
    if (lower === 'kids') return 'Kids';
    if (lower === 'yes') return 'Yes';
    if (lower === 'no') return 'No';
    if (lower === '100% uv protection') return '100% UV protection';
    return titleCaseWords(raw);
  }

  function prettyNewSpecificationTagLabel(value) {
    const raw = cleanText(value);
    const lower = raw.toLowerCase();
    if (!lower.startsWith('new-')) return '';

    const separatorIndex = raw.indexOf(' - ');
    if (separatorIndex < 5) return '';

    const field = cleanText(raw.slice(4, separatorIndex)).toLowerCase();
    const tagValue = cleanText(raw.slice(separatorIndex + 3));
    const label = formatCurrentNewTagValue(tagValue);
    if (!field || !label) return '';
    if (field === 'product type' || field === 'product-type' || field === 'gender' || field === 'age group') return label;
    return `${titleCaseWords(field)}: ${label}`;
  }

  function prettyLabel(param, value) {
    const formLabel = labelFromForm(param, value);
    if (formLabel) return normalizeUnitLabel(param, formLabel);

    const raw = normalizeUnitLabel(param, cleanText(value));
    const lower = raw.toLowerCase().replace(/[’']/g, '');

    if (param === 'filter.v.availability') {
      if (lower === '1' || lower === 'true' || lower === 'available') return 'In stock';
      if (lower === '0' || lower === 'false' || lower === 'unavailable') return 'Out of stock';
    }

    if (param === 'filter.p.tag') {
      const newSpecTagLabel = prettyNewSpecificationTagLabel(raw);
      if (newSpecTagLabel) return newSpecTagLabel;
      if (['mens', 'men', 'male'].includes(lower)) return "Men's";
      if (['womens', 'women', 'female'].includes(lower)) return "Women's";
      if (lower === 'unisex') return 'Unisex';
      if (['kids', 'kid'].includes(lower)) return 'Kids';
      if (['pre-order', 'pre order', 'preorder'].includes(lower)) return 'Pre-order';
      if (['final-price', 'final price', 'finalprice'].includes(lower)) return 'Final price';
      if (['lq', 'limited-quantity', 'limited quantity', 'limitedquantity', 'limited-availability', 'limited availability', 'limitedavailability'].includes(lower)) return 'Limited availability';
      if (['ex-demo', 'ex demo', 'exdemo'].includes(lower)) return 'Ex-Demo';
      if (lower === 'clearance') return 'Clearance';
    }

    if (param === 'filter.p.m.custom.case_diameter_mm' || param === 'filter.p.m.custom.case_thickness_mm') {
      return raw.toLowerCase().includes('mm') ? raw.replace(/\bmm\b/gi, 'mm') : `${raw} mm`;
    }

    if (param === 'filter.p.m.custom.water_resistance_m') {
      return raw.toLowerCase().includes('m') ? raw.replace(/(\d)\s*m\b/gi, '$1 m') : `${raw} m`;
    }

    return raw;
  }

  function getFilterChipsFromUrl() {
    const params = currentUrlParams();
    const chips = [];
    let minPrice = '';
    let maxPrice = '';

    for (const [key, value] of params.entries()) {
      const cleanedValue = cleanText(value);
      if (!key.startsWith('filter.') || cleanedValue === '') continue;

      if (key === 'filter.v.price.gte') {
        minPrice = cleanedValue;
        continue;
      }
      if (key === 'filter.v.price.lte') {
        maxPrice = cleanedValue;
        continue;
      }

      chips.push({
        type: 'single',
        param: key,
        value: cleanedValue,
        label: prettyLabel(key, cleanedValue),
      });
    }

    if (minPrice !== '' || maxPrice !== '') {
      let label;
      if (minPrice !== '' && maxPrice !== '') {
        label = `$${moneyText(minPrice)}-$${moneyText(maxPrice)}`;
      } else if (minPrice !== '') {
        label = `Min $${moneyText(minPrice)}`;
      } else {
        label = `Max $${moneyText(maxPrice)}`;
      }
      chips.push({
        type: 'price',
        param: 'filter.v.price',
        value: `${minPrice}|${maxPrice}`,
        label,
      });
    }

    return chips;
  }

  function buildUrlWithoutChip(chip) {
    const current = currentUrlParams();
    const next = new URLSearchParams();
    let removed = false;

    for (const [key, currentValue] of current.entries()) {
      if (SEARCH_SECTION_KEYS.has(key)) continue;

      if (chip.type === 'price' && (key === 'filter.v.price.gte' || key === 'filter.v.price.lte')) {
        continue;
      }

      if (chip.type !== 'price' && !removed && key === chip.param && cleanText(currentValue) === cleanText(chip.value)) {
        removed = true;
        continue;
      }

      next.append(key, currentValue);
    }

    const query = next.toString();
    return query ? `${window.location.pathname}?${query}` : window.location.pathname;
  }

  function clearAllHref() {
    const current = currentUrlParams();
    const next = new URLSearchParams();
    for (const [key, value] of current.entries()) {
      if (SEARCH_SECTION_KEYS.has(key)) continue;
      if (key.startsWith('filter.')) continue;
      next.append(key, value);
    }
    const query = next.toString();
    return query ? `${window.location.pathname}?${query}` : window.location.pathname;
  }

  function iconCloseSmallSvg() {
    return '<svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close-small" viewBox="0 0 12 13"><path d="M8.48627 9.32917L2.82849 3.67098" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/><path d="M2.88539 9.38504L8.42932 3.61524" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  function paramsFromUrl(url) {
    try {
      const parsedUrl = new URL(String(url || ''), window.location.href);
      return new URLSearchParams(parsedUrl.search || '');
    } catch (error) {
      const rawUrl = String(url || '');
      const queryStart = rawUrl.indexOf('?');
      return new URLSearchParams(queryStart === -1 ? '' : rawUrl.slice(queryStart + 1));
    }
  }

  function cleanedQueryFromUrl(url) {
    const params = paramsFromUrl(url);
    SEARCH_SECTION_KEYS.forEach((key) => params.delete(key));
    return params.toString();
  }

  function hasActiveFilterParams(params) {
    for (const [key, value] of params.entries()) {
      if (key.startsWith('filter.') && cleanText(value) !== '') return true;
    }
    return false;
  }

  function clearFilterFormControls() {
    document.querySelectorAll('#FacetFiltersForm input[name^="filter."], #FacetFiltersFormMobile input[name^="filter."]').forEach((input) => {
      if (input.type === 'checkbox' || input.type === 'radio') {
        input.checked = false;
      } else {
        input.value = '';
      }
    });
  }

  function clearRenderedActiveFacets() {
    document.querySelectorAll('.active-facets-mobile, .active-facets-desktop').forEach((container) => {
      removeExistingChips(container);
      container.dataset.activeFilterCount = '0';
      container.toggleAttribute('hidden', true);
      container.hidden = true;

      const clearWrapper = container.querySelector(CLEAR_WRAPPER_SELECTOR);
      if (clearWrapper) clearWrapper.toggleAttribute('hidden', true);
    });

    document.querySelectorAll('.cosmoshop-filter-toolbar__bottom').forEach((row) => {
      row.dataset.activeFilterCount = '0';
      row.toggleAttribute('hidden', true);
      row.hidden = true;
    });
  }

  function runAfterResetPaint(callback) {
    callback();
    window.setTimeout(callback, 0);
    window.setTimeout(callback, 90);
    window.setTimeout(callback, 250);
  }

  function runFilterUrl(url) {
    const query = cleanedQueryFromUrl(url);
    const params = new URLSearchParams(query);
    const hasFilters = hasActiveFilterParams(params);

    if (!hasFilters) {
      clearFilterFormControls();
      clearRenderedActiveFacets();
    }

    if (window.FacetFiltersForm && typeof window.FacetFiltersForm.renderPage === 'function') {
      window.FacetFiltersForm.toggleActiveFacets();
      window.FacetFiltersForm.renderPage(query);
      if (!hasFilters) runAfterResetPaint(clearRenderedActiveFacets);
      return;
    }
    window.location.href = url;
  }

  function makeChip(chip) {
    const wrapper = document.createElement('span');
    wrapper.setAttribute('data-cosmoshop-url-active-chip', '');

    const link = document.createElement('a');
    link.href = buildUrlWithoutChip(chip);
    link.className = 'active-facets__button active-facets__button--light button';
    link.addEventListener('click', (event) => {
      event.preventDefault();
      if (link.classList.contains('disabled') || link.getAttribute('aria-disabled') === 'true') return;
      runFilterUrl(link.href);
    });

    const inner = document.createElement('span');
    inner.className = 'active-facets__button-inner';
    inner.append(document.createTextNode(chip.label));
    inner.insertAdjacentHTML('beforeend', iconCloseSmallSvg());

    const hiddenText = document.createElement('span');
    hiddenText.className = 'visually-hidden';
    hiddenText.textContent = 'Clear filter';
    inner.append(hiddenText);

    link.append(inner);
    wrapper.append(link);
    return wrapper;
  }

  function removeExistingChips(container) {
    container.querySelectorAll(`${CHIP_SELECTOR}, facet-remove:not(${CLEAR_WRAPPER_SELECTOR})`).forEach((chip) => chip.remove());
  }

  function renderCosmoshopUrlActiveFacets() {
    const row = document.querySelector('.cosmoshop-filter-toolbar__bottom');
    const container = row ? row.querySelector('.active-facets.active-facets-mobile') : document.querySelector('.active-facets.active-facets-mobile');
    if (!row || !container) return;

    const chips = getFilterChipsFromUrl();
    const clearWrapper = container.querySelector(CLEAR_WRAPPER_SELECTOR);

    removeExistingChips(container);

    chips.forEach((chip) => {
      const chipElement = makeChip(chip);
      if (clearWrapper) {
        container.insertBefore(chipElement, clearWrapper);
      } else {
        container.appendChild(chipElement);
      }
    });

    const count = chips.length;
    row.dataset.activeFilterCount = String(count);
    container.dataset.activeFilterCount = String(count);
    row.toggleAttribute('hidden', count === 0);
    container.toggleAttribute('hidden', count === 0);

    if (count > 0) {
      row.hidden = false;
      container.hidden = false;
    }

    if (clearWrapper) {
      clearWrapper.toggleAttribute('hidden', count === 0);
      const clearLink = clearWrapper.querySelector('a');
      if (clearLink) {
        clearLink.href = clearAllHref();
        clearLink.addEventListener('click', (event) => {
          event.preventDefault();
          runFilterUrl(clearLink.href);
        }, { once: true });
      }
    }
  }

  function scheduleRender() {
    window.clearTimeout(window.__cosmoshopActiveFacetTimer);
    window.__cosmoshopActiveFacetTimer = window.setTimeout(renderCosmoshopUrlActiveFacets, 0);
  }

  window.cosmoshopRenderActiveFilterChips = renderCosmoshopUrlActiveFacets;
  window.cosmoshopClearRenderedActiveFilterChips = clearRenderedActiveFacets;

  document.addEventListener('click', (event) => {
    const resetLink = event.target.closest && event.target.closest('a.mobile-facets__clear, a.active-facets__button-remove, .active-facets__button-wrapper > a');
    if (!resetLink) return;

    const query = cleanedQueryFromUrl(resetLink.href);
    const params = new URLSearchParams(query);
    if (hasActiveFilterParams(params)) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    runFilterUrl(resetLink.href);
  }, true);

  document.addEventListener('DOMContentLoaded', scheduleRender);
  window.addEventListener('pageshow', scheduleRender);
  window.addEventListener('popstate', scheduleRender);
  document.addEventListener('shopify:section:load', scheduleRender);

  if (!history.__cosmoshopActiveFacetPatched) {
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;
    history.pushState = function patchedPushState(...args) {
      const result = originalPushState.apply(this, args);
      scheduleRender();
      return result;
    };
    history.replaceState = function patchedReplaceState(...args) {
      const result = originalReplaceState.apply(this, args);
      scheduleRender();
      return result;
    };
    history.__cosmoshopActiveFacetPatched = true;
  }

  if (window.FacetFiltersForm) {
    const originalRenderActiveFacets = window.FacetFiltersForm.renderActiveFacets;
    if (typeof originalRenderActiveFacets === 'function' && !originalRenderActiveFacets.__cosmoshopActiveChipPatched) {
      const patchedRenderActiveFacets = function patchedRenderActiveFacets(...args) {
        const result = originalRenderActiveFacets.apply(this, args);
        scheduleRender();
        return result;
      };
      patchedRenderActiveFacets.__cosmoshopActiveChipPatched = true;
      window.FacetFiltersForm.renderActiveFacets = patchedRenderActiveFacets;
    }

    const originalRenderPage = window.FacetFiltersForm.renderPage;
    if (typeof originalRenderPage === 'function' && !originalRenderPage.__cosmoshopActiveChipPatched) {
      const patchedRenderPage = function patchedRenderPage(...args) {
        const result = originalRenderPage.apply(this, args);
        scheduleRender();
        window.setTimeout(scheduleRender, 80);
        return result;
      };
      patchedRenderPage.__cosmoshopActiveChipPatched = true;
      window.FacetFiltersForm.renderPage = patchedRenderPage;
    }
  }
})();



/***** Cosmoshop filter-control state synchronisation *****/
(() => {
  const FILTER_CONTROL_SELECTOR = '#FacetFiltersForm input[name^="filter."], #FacetFiltersForm select[name^="filter."], #FacetFiltersFormMobile input[name^="filter."], #FacetFiltersFormMobile select[name^="filter."]';
  const PRICE_CONTROL_SELECTOR = '#FacetFiltersForm price-range input[name^="filter."], #FacetFiltersFormMobile price-range input[name^="filter."]';
  const TRANSIENT_QUERY_KEYS = new Set(['page', 'sections', 'section_id']);
  let syncTimer = null;

  function cleanFilterValue(value) {
    let output = String(value == null ? '' : value);
    try {
      output = decodeURIComponent(output.replace(/\+/g, ' '));
    } catch (error) {
      output = output.replace(/\+/g, ' ');
    }
    return output.replace(/\s+/g, ' ').trim().toLowerCase();
  }

  function canonicalPromotionKey(value) {
    const normalized = cleanFilterValue(value);
    if (['pre-order', 'pre order', 'preorder', 'new-promotion - pre-order'].includes(normalized)) return 'pre-order';
    if (['final-price', 'final price', 'finalprice', 'new-promotion - final price'].includes(normalized)) return 'final-price';
    if (['ex-demo', 'ex demo', 'exdemo', 'new-promotion - ex-demo'].includes(normalized)) return 'ex-demo';
    if (['lq', 'limited-quantity', 'limited quantity', 'limitedquantity', 'limited-availability', 'limited availability', 'limitedavailability', 'new-promotion - limited quantity', 'new-promotion - limited availability'].includes(normalized)) return 'limited-quantity';
    return '';
  }

  function getCurrentFilterParams(searchParamsOverride = null) {
    const source = searchParamsOverride == null ? (window.location.search || '') : String(searchParamsOverride || '');
    const params = new URLSearchParams(source.charAt(0) === '?' ? source.slice(1) : source);
    TRANSIENT_QUERY_KEYS.forEach((key) => params.delete(key));
    return params;
  }

  function selectedValuesByName(params) {
    const selected = new Map();
    for (const [name, value] of params.entries()) {
      if (!name.startsWith('filter.')) continue;
      if (!selected.has(name)) selected.set(name, []);
      selected.get(name).push(value);
    }
    return selected;
  }

  function controlMatchesUrl(control, selected) {
    const name = control.getAttribute('name') || '';
    const selectedValues = selected.get(name) || [];
    if (!selectedValues.length) return false;

    const promotionKey = control.dataset.cosmoshopPromotionKey
      || control.closest('[data-cosmoshop-promotion-key]')?.dataset?.cosmoshopPromotionKey
      || '';

    if (name === 'filter.p.tag' && promotionKey) {
      return selectedValues.some((value) => canonicalPromotionKey(value) === promotionKey);
    }

    const controlValue = cleanFilterValue(control.value);
    return selectedValues.some((value) => cleanFilterValue(value) === controlValue);
  }

  function syncLabelMetadata(control) {
    const label = control.closest('label[data-cosmoshop-filter-option]');
    if (!label) return;
    label.dataset.cosmoshopFilterParam = control.name || '';
    label.dataset.cosmoshopFilterValue = control.value || '';
    const promotionKey = control.dataset.cosmoshopPromotionKey || label.dataset.cosmoshopPromotionKey || '';
    if (promotionKey) {
      label.dataset.cosmoshopPromotionKey = promotionKey;
      control.dataset.cosmoshopPromotionKey = promotionKey;
    }
  }

  function syncCheckboxOrRadio(control, selected) {
    const shouldBeChecked = controlMatchesUrl(control, selected);
    if (control.checked !== shouldBeChecked) control.checked = shouldBeChecked;
    control.setAttribute('aria-checked', shouldBeChecked ? 'true' : 'false');
    syncLabelMetadata(control);
  }

  function syncValueControl(control, params) {
    const name = control.getAttribute('name') || '';
    if (!name.startsWith('filter.')) return;
    const nextValue = params.get(name) || '';
    if (String(control.value || '') !== String(nextValue)) control.value = nextValue;
  }

  function syncFilterControlsFromUrl(searchParamsOverride = null) {
    const params = getCurrentFilterParams(searchParamsOverride);
    const selected = selectedValuesByName(params);

    document.querySelectorAll(FILTER_CONTROL_SELECTOR).forEach((control) => {
      if (control.matches('input[type="checkbox"], input[type="radio"]')) {
        syncCheckboxOrRadio(control, selected);
      } else if (control.matches('input, select')) {
        syncValueControl(control, params);
      }
    });

    document.querySelectorAll(PRICE_CONTROL_SELECTOR).forEach((control) => {
      syncValueControl(control, params);
    });
  }

  function scheduleFilterControlSync(delay = 0) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncFilterControlsFromUrl, delay);
  }

  window.cosmoshopSyncFilterControlsFromUrl = syncFilterControlsFromUrl;
  window.cosmoshopSyncFilterControlsFromSearchParams = syncFilterControlsFromUrl;

  document.addEventListener('DOMContentLoaded', () => scheduleFilterControlSync(0));
  window.addEventListener('pageshow', () => scheduleFilterControlSync(0));
  window.addEventListener('popstate', () => {
    scheduleFilterControlSync(0);
    scheduleFilterControlSync(80);
  });
  document.addEventListener('shopify:section:load', () => scheduleFilterControlSync(0));

  if (!history.__cosmoshopFilterControlSyncPatched) {
    const originalPushState = history.pushState;
    const originalReplaceState = history.replaceState;

    history.pushState = function cosmoshopFilterControlSyncPushState(...args) {
      const result = originalPushState.apply(this, args);
      scheduleFilterControlSync(0);
      return result;
    };

    history.replaceState = function cosmoshopFilterControlSyncReplaceState(...args) {
      const result = originalReplaceState.apply(this, args);
      scheduleFilterControlSync(0);
      return result;
    };

    history.__cosmoshopFilterControlSyncPatched = true;
  }

  if (window.FacetFiltersForm && !window.FacetFiltersForm.__cosmoshopFilterControlSyncPatched) {
    const originalRenderFilters = window.FacetFiltersForm.renderFilters;
    if (typeof originalRenderFilters === 'function') {
      window.FacetFiltersForm.renderFilters = function cosmoshopFilterControlSyncRenderFilters(...args) {
        const result = originalRenderFilters.apply(this, args);
        scheduleFilterControlSync(0);
        scheduleFilterControlSync(80);
        return result;
      };
    }

    const originalRenderSectionResponse = window.FacetFiltersForm.renderSectionResponse;
    if (typeof originalRenderSectionResponse === 'function') {
      window.FacetFiltersForm.renderSectionResponse = function cosmoshopFilterControlSyncRenderSectionResponse(...args) {
        const result = originalRenderSectionResponse.apply(this, args);
        scheduleFilterControlSync(0);
        return result;
      };
    }

    window.FacetFiltersForm.__cosmoshopFilterControlSyncPatched = true;
  }
})();
